import React, { useCallback, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { api, mutate, downloadFile } from '../shared/api';
import { Crumbs, Card, Btn, Pill, DataTable, ui, type Col } from '../shared/ui';
import { DateInput } from '../shared/DateInput';
import { PaymentTrafficSettings } from './paymentTrafficSettings';

type Locale = 'hr' | 'en';

type ReasonCode = 'otkup_lom' | 'otkup_invest' | 'dnevna_gotovina' | 'provjera_uplate' | 'virman_klijent' | 'upit_o_uplati' | 'ostalo';

type PaymentRequest = {
    id: number;
    public_id: string;
    amount_eur: number | string;
    reason_code: ReasonCode;
    reason_notes: string | null;
    status: 'ceka_voditelja' | 'vraceno_na_doradu' | 'ceka_posebno_odobrenje' | 'pending' | 'approved' | 'executed' | 'received_branch' | 'rejected' | 'zavrseno' | 'otkazano' | 'problematicno';
    created_at: string;
    deleted_at?: string | null;
    approved_at: string | null;
    executed_at: string | null;
    ris_reference: string | null;
    received_at: string | null;
    rejected_at: string | null;
    rejection_reason: string | null;
    admin_notes: string | null;
    branch_dept_id: number | null;
    branch_name: string | null;
    branch_code: string | null;
    requester_user_id: number | null;
    requester_name: string | null;
    approver_name: string | null;
    executor_name: string | null;
    receiver_name: string | null;
    target_bank_account_id: number | null;
    target_iban: string | null;
    target_label: string | null;
    target_bank: string | null;
    target_company_code: string | null;
    proof_document_path: string | null;
    proof_document_name: string | null;
    source_subaccount_id: number | null;
    customer_address: string | null;
    customer_city: string | null;
    payment_model: string | null;
    payment_reference: string | null;
    opis_placanja: string | null;
    has_customer_name: number | boolean;
    has_customer_iban: number | boolean;
    problem_note?: string | null;
    resubmit_count?: number;
    finalized_at?: string | null;
    i_can_approve?: boolean;
    match_status?: string | null;
    data_source?: string | null;   // 'pdf' = dignuto iz kontrolnog dokumenta, 'manual' = ručno
    offer_order_ref?: string | null;
    parent_request_id?: number | null;
    parent_public_id?: string | null;
    split_group_id?: number | null;
    // UID 652: isti dokument (offer_order_ref) već je bio u sustavu — veza na raniju pojavnost.
    // Otkriveno, NE blokira unos; mora biti vidljivo operateru.
    duplicate_of_request_id?: number | null;
    duplicate_of_public_id?: string | null;
    duplicate_of_status?: PaymentRequest['status'] | null;
};

const MATCH_META: Record<string, { icon: string; hr: string; en: string; color: string }> = {
    matched: { icon: '✓', hr: 'Upareno s feedom', en: 'Matched to feed', color: '#1f7a36' },
    manual: { icon: '✓', hr: 'Ručno upareno', en: 'Manually matched', color: '#1f7a36' },
    ambiguous: { icon: '?', hr: 'Dvojbeno (više kandidata)', en: 'Ambiguous', color: '#c2701a' },
    unmatched: { icon: '✗', hr: 'Nespareno', en: 'Unmatched', color: '#b14040' },
};

function matchBadge(r: PaymentRequest, locale: Locale) {
    // Badge se prikazuje samo za tipove s PDF importom (otkup); dnevna gotovina i priljevi ga nemaju.
    if (r.reason_code !== 'otkup_lom' && r.reason_code !== 'otkup_invest') return <span style={{ color: '#ccc' }}>—</span>;
    const hr = locale !== 'en';
    // 1) Porijeklo naloga: dignuto iz PDF-a (ispravan, zeleno) vs ručno upisano (crveno).
    const isPdf = r.data_source === 'pdf';
    const nalogBadge = (
        <span style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, color: '#fff', background: isPdf ? '#1f7a36' : '#b14040' }}>
            {isPdf ? (hr ? '✓ Ispravan nalog' : '✓ Verified order') : (hr ? '✎ Ručni nalog' : '✎ Manual order')}
        </span>
    );
    // 2) Status uparenosti s feedom (ispod).
    const m = r.match_status ? MATCH_META[r.match_status] : null;
    const matchLine = m
        ? <span style={{ fontSize: '0.72rem', color: m.color, fontWeight: 600 }}>{m.icon} {hr ? m.hr : m.en}</span>
        : <span style={{ fontSize: '0.7rem', color: '#bbb' }}>{hr ? 'nije provjereno' : 'unchecked'}</span>;
    return <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>{nalogBadge}{matchLine}</span>;
}

/** „Isplata na" ćelija: klijentski IBAN → račun klijenta (transakcijski); inače (gotovinski otkup /
 *  dnevna gotovina) → podračun poslovnice (odabran ili ga admin bira). Vrijedi za SVE tipove. */
function payoutToCell(r: PaymentRequest, locale: Locale) {
    const hr = locale !== 'en';
    if (Number(r.has_customer_iban) === 1) return <span style={{ fontSize: '0.78rem', color: '#1e40af', fontWeight: 600 }}>📨 {hr ? 'Račun klijenta' : 'Client account'}</span>;
    if (r.target_bank_account_id != null || INTERNAL_PAYOUT_REASONS.includes(r.reason_code)) {
        // Odredište interni virman: sustav ga izvodi iz firme (invest→ADO / lom→ADMS) + poslovnice.
        // Firma (ADO/ADMS) MORA biti vidljiva ovdje: label podračuna sam po sebi (npr. samo naziv
        // poslovnice) ne razlikuje ADO podračun te poslovnice od ADMS podračuna iste poslovnice
        // (mail UID 551, Sandra Nišić/Zagreb East — blagajna nije znala na koji podračun uplatiti).
        return (
            <span style={{ fontSize: '0.78rem', color: '#6b4f1c', fontWeight: 600 }}>
                🏦 {r.target_company_code
                    ? <span style={{ background: '#6b4f1c', color: '#fff', borderRadius: 4, padding: '1px 6px', marginRight: 4, fontWeight: 700 }}>{r.target_company_code}</span>
                    : null}
                {hr ? 'Podračun poslovnice' : 'Branch sub-account'}{r.target_label ? ' · ' + r.target_label : ''}{r.target_bank_account_id == null ? ' · ' + (hr ? 'automatski (firma)' : 'auto (company)') : ''}
            </span>
        );
    }
    return <span style={{ fontSize: '0.78rem', color: '#666' }}>{r.target_label ?? '—'}{r.target_bank ? ' · ' + r.target_bank : ''}</span>;
}

/** UID 652: dokument je već jednom bio u sustavu (isti offer_order_ref/poslovnica/tip). Ne blokira
 *  unos, ali MORA biti jasno vidljivo operateru — narančasti badge s vezom na raniju pojavnost.
 *  onJump (opcijski) otvara raniji zahtjev ako je u trenutno učitanoj listi. */
function duplicateBadge(r: PaymentRequest, locale: Locale, onJump?: (publicId: string) => void) {
    if (!r.duplicate_of_public_id) return null;
    const hr = locale !== 'en';
    return (
        <span
            title={hr ? `Ovaj dokument je već ranije unesen kao ${r.duplicate_of_public_id}. Provjerite da ne dolazi do dvostruke isplate.` : `This document was already entered before as ${r.duplicate_of_public_id}. Check this does not cause a double payout.`}
            onClick={onJump ? (e) => { e.stopPropagation(); onJump(r.duplicate_of_public_id as string); } : undefined}
            style={{
                display: 'inline-block', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                color: '#fff', background: '#b8621a', marginTop: 2,
                cursor: onJump ? 'pointer' : 'default', textDecoration: onJump ? 'underline' : 'none',
            }}>
            ⚠ {hr ? 'Dokument već korišten' : 'Document already used'}: {r.duplicate_of_public_id}
        </span>
    );
}

type State = {
    is_admin: boolean;
    my_user_id?: number;
    is_owner?: boolean;
    deleted_count?: number;
    is_admin_role?: boolean;
    pt_role?: 'user' | 'voditelj' | 'manager' | 'provjera_uplate';
    is_manager?: boolean;
    my_branch_ids: number[];
    voditelj_branch_ids?: number[];
    queue_count: number;
    pending_count?: number;
    in_progress_count?: number;
    my_pending: number;
    my_authored?: number;
    waiting_for_me?: number;
    reason_codes: ReasonCode[];
    type_requires_voditelj?: Record<string, boolean>;
    notify_payment_requests?: boolean;
    pt_notify_sms?: boolean;
    pt_notify_email?: boolean;
    pt_notify_gong?: boolean;
    sms_configured?: boolean;
};

type Branch = { id: number; code: string | null; name: string; company_id: number; json_pos_id: string | null };

type DailyItem = { public_id: string; branch: string; reason: string; amount: number; status: string; order: string };
type DailySub = { label: string | null; n: number; sum: number; rows: DailyItem[] };
type DailyGroup = { label: string; n: number; sum: number; sub: DailySub[] };
type DailyReport = {
    date: string; date_label: string; total_count: number; total_eur: number;
    by_company: DailyGroup[]; by_branch: DailyGroup[]; by_type: DailyGroup[];
};

type BankAccount = {
    id: number;
    iban: string;
    bank_name: string;
    account_label: string;
    purpose: string;
    branch_dept_id: number | null;
    branch_name?: string | null;
    company_id: number | null;
    lifecycle_status: string;
    is_fallback?: number | boolean;
    bank_code?: string | null;
};

type PayoutPreview = {
    amount_eur: number;
    payout_amount_eur: number;
    rounded_up: boolean;
    client_bank: { code: string | null; name: string | null; short: string | null };
    client_iban_valid: boolean | null;
    payout_account: { account_id: number | null; resolution: string; iban: string | null; bank_name: string | null; bank_short: string | null };
};

type ParsedDoc = {
    ok: boolean;
    nacin_isplate: string | null;
    reason_code: ReasonCode | null;
    pays_to: string | null;            // 'branch_subaccount' | 'client_iban'
    customer_name: string | null;
    customer_oib: string | null;
    amount_eur: number | null;
    poslovnica_text: string | null;
    offer_order_ref: string | null;
    matched_branch_id: number | null;
    matched_branch_name: string | null;
    needs_client_iban: boolean;
};

const PAYOUT_RESOLUTION_LABEL: Record<string, [string, string]> = {
    same_bank_sub: ['podračun iste banke', 'same-bank sub-account'],
    same_bank_main: ['glavni račun iste banke', 'same-bank main account'],
    fallback: ['zadani (fallback) račun', 'default (fallback) account'],
    company_main: ['glavni račun firme', 'company main account'],
    manual: ['ručno odabran', 'manually chosen'],
    none: ['nije određen', 'not determined'],
};


const REASON_META: Record<ReasonCode, { hr: string; en: string; icon: string; color: string }> = {
    otkup_lom: { hr: 'Otkup loma', en: 'Scrap buyback', icon: '⚒', color: 'var(--ad-ink)' },
    otkup_invest: { hr: 'Otkup investicijskog', en: 'Investment buyback', icon: '🟡', color: 'var(--ad-acc)' },
    dnevna_gotovina: { hr: 'Dnevna gotovina', en: 'Daily cash', icon: '💵', color: '#1f7a36' },
    provjera_uplate: { hr: 'Provjera uplate', en: 'Payment check', icon: '💰', color: '#1e40af' },
    virman_klijent: { hr: 'Virman klijentu', en: 'Customer wire', icon: '📨', color: '#b14040' },
    upit_o_uplati: { hr: 'Upit o uplati', en: 'Payment inquiry', icon: '💰', color: '#1e40af' },
    ostalo: { hr: 'Ostalo', en: 'Other', icon: '•', color: '#888' },
};
// Tiles ponuđeni pri kreiranju (virman_klijent + upit_o_uplati zamijenjeni s provjera_uplate; ostaju samo za legacy prikaz).
const REASON_SELECTABLE: ReasonCode[] = ['otkup_lom', 'otkup_invest', 'dnevna_gotovina', 'provjera_uplate', 'ostalo'];
// Priljev (klijent plaća nama) — bez isplate/računa; potvrđuje se „Uplata je vidljiva".
const INFLOW_REASONS: ReasonCode[] = ['provjera_uplate', 'upit_o_uplati'];
// Interni virman (nadoknada blagajne) — odredište izvodi sustav iz firme (invest→ADO / lom→ADMS) + poslovnice.
const INTERNAL_PAYOUT_REASONS: ReasonCode[] = ['dnevna_gotovina'];

const STATUS_META: Record<PaymentRequest['status'], { hr: string; en: string; color: string; bg: string }> = {
    ceka_voditelja: { hr: '👤 Čeka voditelja', en: '👤 Awaiting lead', color: '#fff', bg: '#9a6a1a' },
    ceka_posebno_odobrenje: { hr: '⭐ Čeka posebno odobrenje', en: '⭐ Special approval', color: '#fff', bg: '#7c3aed' },
    vraceno_na_doradu: { hr: '↩ Vraćeno na doradu', en: '↩ Returned', color: '#fff', bg: '#c2701a' },
    pending: { hr: '⏳ Kod managera', en: '⏳ At manager', color: '#fff', bg: 'var(--ad-acc)' },
    approved: { hr: '✓ Odobreno', en: '✓ Approved', color: '#fff', bg: '#1e40af' },
    executed: { hr: '🚀 Izvršeno', en: '🚀 Executed', color: '#fff', bg: 'var(--ad-ink)' },
    received_branch: { hr: '✅ U blagajni', en: '✅ Received', color: '#fff', bg: '#1f7a36' },
    zavrseno: { hr: '✅ Završeno', en: '✅ Done', color: '#fff', bg: '#1f7a36' },
    otkazano: { hr: '⊘ Otkazano', en: '⊘ Cancelled', color: '#fff', bg: '#777' },
    problematicno: { hr: '⚠ Problematično', en: '⚠ Problematic', color: '#fff', bg: '#b14040' },
    rejected: { hr: '✕ Odbijeno', en: '✕ Rejected', color: '#fff', bg: '#b14040' },
};

const PT_BTN: Record<string, React.CSSProperties> = {
    g: { background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer' },
    o: { background: '#c2701a', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer' },
    r: { background: '#b14040', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer' },
    b: { background: '#1e40af', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer' },
    dark: { background: 'var(--ad-ink)', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer' },
    gray: { background: '#777', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer' },
};

const PT_EVENT_LABELS: Record<string, [string, string]> = {
    created: ['Zahtjev kreiran', 'Request created'],
    resubmitted: ['Ponovno poslano (dorada)', 'Resubmitted'],
    voditelj_approved: ['Voditelj odobrio', 'Lead approved'],
    voditelj_returned: ['Voditelj vratio na doradu', 'Lead returned'],
    voditelj_rejected: ['Voditelj odbio', 'Lead rejected'],
    posebno_approved: ['Posebno odobreno', 'Special approved'],
    posebno_returned: ['Vraćeno na doradu (posebno)', 'Returned (special)'],
    posebno_rejected: ['Odbijeno (posebno)', 'Rejected (special)'],
    cancelled: ['Najava otkazana', 'Request cancelled'],
    approved: ['Odobreno', 'Approved'],
    executed: ['Izvršeno', 'Executed'],
    received: ['Primljeno u blagajnu', 'Received in cash desk'],
    rejected: ['Odbijeno', 'Rejected'],
    finalized: ['Finalni status', 'Finalized'],
    chain_bypassed: ['Lanac preskočen (superadmin)', 'Chain bypassed'],
};

function ptEventLabel(ev: string, locale: Locale): string {
    const pair = PT_EVENT_LABELS[ev];
    if (!pair) return ev;
    return locale === 'en' ? pair[1] : pair[0];
}

type PtTab = 'requests' | 'settings' | 'roster';

function PtTabBar({ tab, setTab, t, waiting, showSettings }: { tab: PtTab; setTab: (v: PtTab) => void; t: (hr: string, en: string) => string; waiting?: number; showSettings?: boolean }) {
    const btn = (active: boolean): React.CSSProperties => ({
        padding: '6px 16px', background: active ? 'var(--ad-ink)' : 'transparent', color: active ? '#fff' : 'var(--ad-ink)',
        border: '1px solid var(--ad-ink)', borderRadius: 8, fontSize: '0.84rem', fontWeight: 700, cursor: 'pointer',
    });
    return (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button type="button" onClick={() => setTab('requests')} style={btn(tab === 'requests')}>📋 {t('Zahtjevi', 'Requests')}{waiting ? ` (${waiting})` : ''}</button>
            <button type="button" onClick={() => setTab('roster')} style={btn(tab === 'roster')}>🗓 {t('Raspored rada', 'Duty roster')}</button>
            {showSettings ? <button type="button" onClick={() => setTab('settings')} style={btn(tab === 'settings')}>⚙ {t('Postavke', 'Settings')}</button> : null}
        </div>
    );
}

type DutyEntry = {
    id: number;
    work_date: string;
    shift_label: string | null;
    time_from: string | null;
    time_to: string | null;
    user_id: number;
    user_name: string | null;
    backup_user_id: number | null;
    backup_user_name: string | null;
    note: string | null;
};

type DutyUser = { id: number; name: string };

const DUTY_WEEKDAY_LABELS: Array<[string, string]> = [
    ['Pon', 'Mon'], ['Uto', 'Tue'], ['Sri', 'Wed'], ['Čet', 'Thu'], ['Pet', 'Fri'], ['Sub', 'Sat'], ['Ned', 'Sun'],
];

function mondayOf(d: Date): Date {
    const x = new Date(d);
    const day = x.getDay(); // 0=Sun..6=Sat
    const diff = (day === 0 ? -6 : 1 - day);
    x.setDate(x.getDate() + diff);
    x.setHours(0, 0, 0, 0);
    return x;
}
const isoOfDate = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Modal za dodavanje/uređivanje jednog dežurstva u rasporedu rada (m.21). */
function DutyEntryModal({ token, locale, users, initial, defaultDate, onClose, onSaved }: {
    token: string | null; locale: Locale; users: DutyUser[]; initial: DutyEntry | null; defaultDate: string;
    onClose: () => void; onSaved: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [workDate, setWorkDate] = useState(initial?.work_date ?? defaultDate);
    const [shiftLabel, setShiftLabel] = useState(initial?.shift_label ?? '');
    const [timeFrom, setTimeFrom] = useState(initial?.time_from ? initial.time_from.slice(0, 5) : '');
    const [timeTo, setTimeTo] = useState(initial?.time_to ? initial.time_to.slice(0, 5) : '');
    const [userId, setUserId] = useState<number | ''>(initial?.user_id ?? '');
    const [backupUserId, setBackupUserId] = useState<number | ''>(initial?.backup_user_id ?? '');
    const [note, setNote] = useState(initial?.note ?? '');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!userId) { alert(t('Odaberi odgovornu osobu.', 'Pick the responsible person.')); return; }
        setSaving(true);
        try {
            const body = {
                work_date: workDate,
                shift_label: shiftLabel.trim() || null,
                time_from: timeFrom || null,
                time_to: timeTo || null,
                user_id: userId,
                backup_user_id: backupUserId || null,
                note: note.trim() || null,
            };
            if (initial) await mutate(`/payment-traffic/duty-roster/${initial.id}`, token, 'PATCH', body);
            else await mutate('/payment-traffic/duty-roster', token, 'POST', body);
            onSaved();
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setSaving(false); }
    };

    const field: React.CSSProperties = { width: '100%', padding: '6px 10px', border: '1px solid var(--ad-acc)', borderRadius: 8, fontSize: '0.86rem' };

    return (
        <Modal onClose={onClose}>
            <h3 style={{ margin: '0 0 12px', color: 'var(--ad-ink)' }}>{initial ? t('Uredi dežurstvo', 'Edit duty') : t('Novo dežurstvo', 'New duty')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--ad-ink)' }}>{t('Datum', 'Date')}<DateInput value={workDate} onChange={setWorkDate} /></label>
                <label style={{ fontSize: '0.8rem', color: 'var(--ad-ink)' }}>{t('Smjena / oznaka', 'Shift / label')}
                    <input type="text" value={shiftLabel} onChange={(e) => setShiftLabel(e.target.value)} placeholder={t('npr. Jutro, Popodne, Cijeli dan', 'e.g. Morning, Afternoon, Full day')} style={field} />
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{ flex: 1, fontSize: '0.8rem', color: 'var(--ad-ink)' }}>{t('Od', 'From')}<input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} style={field} /></label>
                    <label style={{ flex: 1, fontSize: '0.8rem', color: 'var(--ad-ink)' }}>{t('Do', 'To')}<input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} style={field} /></label>
                </div>
                <label style={{ fontSize: '0.8rem', color: 'var(--ad-ink)' }}>{t('Odgovorna osoba', 'Responsible person')}
                    <select value={userId} onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : '')} style={field}>
                        <option value="">{t('— odaberi —', '— pick —')}</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: '0.8rem', color: 'var(--ad-ink)' }}>{t('Zamjena (opcionalno)', 'Backup (optional)')}
                    <select value={backupUserId} onChange={(e) => setBackupUserId(e.target.value ? Number(e.target.value) : '')} style={field}>
                        <option value="">—</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: '0.8rem', color: 'var(--ad-ink)' }}>{t('Napomena', 'Note')}
                    <input type="text" value={note} onChange={(e) => setNote(e.target.value)} style={field} />
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                    <Btn variant="ghost" onClick={onClose}>{t('Odustani', 'Cancel')}</Btn>
                    <Btn variant="primary" onClick={() => void save()} disabled={saving}>{saving ? t('Spremam…', 'Saving…') : t('Spremi', 'Save')}</Btn>
                </div>
            </div>
        </Modal>
    );
}

/**
 * Tab "Raspored rada" (m.21) — tjedni prikaz tko je kada dežuran. Pregled: svi s pristupom
 * modulu. Uređivanje (dodaj/uredi/obriši): samo voditelj/manager/admin — server vraća
 * can_manage i to je jedini izvor istine; frontend samo sakriva kontrole kad je false.
 */
function PtDutyRosterView({ token, locale }: { token: string | null; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));
    const [rows, setRows] = useState<DutyEntry[]>([]);
    const [users, setUsers] = useState<DutyUser[]>([]);
    const [canManage, setCanManage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState<{ entry: DutyEntry | null; date: string } | null>(null);

    const days = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }), [weekStart]);
    const fromIso = isoOfDate(days[0]);
    const toIso = isoOfDate(days[6]);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const r = await api<{ rows: DutyEntry[]; can_manage: boolean; users: DutyUser[] }>(`/payment-traffic/duty-roster?from=${fromIso}&to=${toIso}`, token);
            setRows(r.rows ?? []);
            setCanManage(!!r.can_manage);
            setUsers(r.users ?? []);
        } catch { /* */ }
        finally { setLoading(false); }
    }, [token, fromIso, toIso]);

    useEffect(() => { void load(); }, [load]);

    const removeEntry = async (id: number) => {
        if (!window.confirm(t('Obrisati dežurstvo?', 'Delete this duty?'))) return;
        try { await mutate(`/payment-traffic/duty-roster/${id}`, token, 'DELETE'); void load(); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };

    const rowsByDate = useMemo(() => {
        const m = new Map<string, DutyEntry[]>();
        for (const r of rows) {
            const list = m.get(r.work_date) ?? [];
            list.push(r);
            m.set(r.work_date, list);
        }
        return m;
    }, [rows]);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <Btn size="sm" variant="ghost" onClick={() => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}>← {t('Prošli tjedan', 'Prev week')}</Btn>
                <div style={{ fontWeight: 700, color: 'var(--ad-ink)' }}>{fromIso} — {toIso}</div>
                <Btn size="sm" variant="ghost" onClick={() => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}>{t('Sljedeći tjedan', 'Next week')} →</Btn>
                <Btn size="sm" variant="ghost" onClick={() => setWeekStart(mondayOf(new Date()))}>{t('Ovaj tjedan', 'This week')}</Btn>
                {loading ? <span style={{ fontSize: '0.78rem', color: ui.muted }}>{t('Učitavam…', 'Loading…')}</span> : null}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(150px, 1fr))', gap: 8 }}>
                {days.map((d, i) => {
                    const iso = isoOfDate(d);
                    const entries = rowsByDate.get(iso) ?? [];
                    const isToday = iso === isoOfDate(new Date());
                    return (
                        <div key={iso} style={{ border: '1px solid var(--ad-acc)', borderRadius: 10, padding: 8, background: isToday ? '#fff7e0' : '#fff', minHeight: 140 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--ad-ink)', marginBottom: 6 }}>
                                {t(DUTY_WEEKDAY_LABELS[i][0], DUTY_WEEKDAY_LABELS[i][1])} · {iso.slice(8, 10)}.{iso.slice(5, 7)}.
                            </div>
                            {entries.length === 0 ? <div style={{ fontSize: '0.74rem', color: '#aaa', fontStyle: 'italic' }}>{t('Nema unosa', 'No entries')}</div> : null}
                            {entries.map((e) => (
                                <div key={e.id} style={{ background: '#faf6ec', border: '1px solid #efe6d2', borderRadius: 8, padding: 6, marginBottom: 6, fontSize: '0.76rem' }}>
                                    {e.shift_label ? <div style={{ fontWeight: 700, color: '#6b4f1c' }}>{e.shift_label}</div> : null}
                                    {(e.time_from || e.time_to) ? <div style={{ color: '#8a7a55' }}>{(e.time_from ?? '').slice(0, 5)}{e.time_to ? '–' + e.time_to.slice(0, 5) : ''}</div> : null}
                                    <div style={{ fontWeight: 700 }}>{e.user_name ?? '—'}</div>
                                    {e.backup_user_name ? <div style={{ color: '#8a7a55' }}>{t('zamjena', 'backup')}: {e.backup_user_name}</div> : null}
                                    {e.note ? <div style={{ color: '#8a7a55', fontStyle: 'italic' }}>{e.note}</div> : null}
                                    {canManage ? (
                                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                            <button type="button" onClick={() => setModal({ entry: e, date: iso })} style={{ background: 'none', border: 0, color: 'var(--ad-ink)', cursor: 'pointer', fontSize: '0.72rem', padding: 0 }}>✎ {t('uredi', 'edit')}</button>
                                            <button type="button" onClick={() => void removeEntry(e.id)} style={{ background: 'none', border: 0, color: '#b14040', cursor: 'pointer', fontSize: '0.72rem', padding: 0 }}>✕ {t('obriši', 'delete')}</button>
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                            {canManage ? (
                                <button type="button" onClick={() => setModal({ entry: null, date: iso })} style={{ width: '100%', padding: '4px 0', background: 'transparent', border: '1px dashed var(--ad-acc)', borderRadius: 6, color: 'var(--ad-ink)', fontSize: '0.72rem', cursor: 'pointer' }}>+ {t('dodaj', 'add')}</button>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            {!canManage ? <p style={{ marginTop: 12, fontSize: '0.78rem', color: ui.muted }}>{t('Pregled je dostupan svima s pristupom modulu; uređivanje rasporeda smiju voditelji i administratori.', 'Everyone with module access can view; only leads/admins can edit the roster.')}</p> : null}

            {modal ? (
                <DutyEntryModal token={token} locale={locale} users={users} initial={modal.entry} defaultDate={modal.date}
                    onClose={() => setModal(null)} onSaved={() => { setModal(null); void load(); }} />
            ) : null}
        </div>
    );
}

const fmtEur = (v: number | string): string => new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v)) + ' €';
const fmtDateTime = (s: string | null): string => {
    if (!s) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
    return m ? `${m[3]}.${m[2]}.${m[1]}. ${m[4]}:${m[5]}` : s;
};

/** Dnevni izvještaj — overlay s podacima (3 grupiranja) + odabir PDF/XLS. Sve brendirano. */
function DailyReportModal({ token, locale, date, data, loading, onDateChange, onClose }: {
    token: string | null; locale: Locale; date: string; data: DailyReport | null; loading: boolean;
    onDateChange: (d: string) => void; onClose: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [view, setView] = React.useState<'by_company' | 'by_branch' | 'by_type'>('by_company');
    const groups = data ? data[view] : [];
    const dl = (fmt: 'pdf' | 'xlsx') => {
        const ext = fmt === 'pdf' ? 'pdf' : 'xlsx';
        void downloadFile(`/payment-traffic/daily-report${fmt === 'pdf' ? '/pdf' : ''}?date=${date}`, token, `platni_promet_dnevni_${date}.${ext}`);
    };
    return (
        <Modal onClose={onClose} wide>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,var(--ad-acc),var(--ad-ink))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📊</div>
                    <div>
                        <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>{t('Dnevni izvještaj', 'Daily report')}</h3>
                        <div style={{ fontSize: '0.78rem', color: '#8a7a55' }}>{t('Platni promet', 'Payment traffic')} · {data?.date_label ?? date}</div>
                    </div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ width: 150 }}><DateInput value={date} onChange={(v) => onDateChange(v)} /></div>
                    <button type="button" onClick={() => dl('pdf')} disabled={!data || !data.total_count} style={{ background: '#b14040', color: '#fff', border: 0, borderRadius: 8, padding: '7px 14px', fontWeight: 700, cursor: data?.total_count ? 'pointer' : 'default', opacity: data?.total_count ? 1 : 0.5 }}>⬇ PDF</button>
                    <button type="button" onClick={() => dl('xlsx')} disabled={!data || !data.total_count} style={{ background: '#1f7a36', color: '#fff', border: 0, borderRadius: 8, padding: '7px 14px', fontWeight: 700, cursor: data?.total_count ? 'pointer' : 'default', opacity: data?.total_count ? 1 : 0.5 }}>⬇ XLS</button>
                </div>
            </div>

            {loading ? <div style={{ padding: 30, textAlign: 'center', color: '#8a7a55' }}>{t('Učitavam…', 'Loading…')}</div> : !data ? null : data.total_count === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#8a7a55', fontStyle: 'italic' }}>{t('Nema zahtjeva za ovaj dan.', 'No requests for this day.')}</div>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                        <div style={{ background: '#faf6ec', border: '1px solid #efe6d2', borderRadius: 10, padding: '8px 16px' }}>
                            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--ad-ink)' }}>{data.total_count}</div>
                            <div style={{ fontSize: '0.72rem', color: '#8a7a55' }}>{t('zahtjeva', 'requests')}</div>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg,#fff7e0,#faf0d0)', border: '1px solid #e6d8b0', borderRadius: 10, padding: '8px 16px' }}>
                            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#6b4f1c' }}>{fmtEur(data.total_eur)}</div>
                            <div style={{ fontSize: '0.72rem', color: '#8a7a55' }}>{t('ukupno', 'total')}</div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                        {([['by_company', t('Po firmi', 'By company')], ['by_branch', t('Po poslovnici', 'By branch')], ['by_type', t('Po tipu', 'By type')]] as const).map(([k, lbl]) => (
                            <button key={k} type="button" onClick={() => setView(k)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (view === k ? 'var(--ad-ink)' : '#e0d4b6'), background: view === k ? 'var(--ad-ink)' : '#fff', color: view === k ? '#fff' : 'var(--ad-ink)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>{lbl}</button>
                        ))}
                    </div>

                    <div style={{ maxHeight: '58vh', overflowY: 'auto', border: '1px solid #efe6d2', borderRadius: 10 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                            <thead style={{ position: 'sticky', top: 0 }}>
                                <tr style={{ background: '#f4ecd6' }}>
                                    {['Broj', 'Poslovnica', 'Razlog', 'Iznos', 'Status', 'Narudžba'].map((h, i) => (
                                        <th key={h} style={{ textAlign: i === 3 ? 'right' : 'left', padding: '7px 9px', color: '#6b4f1c', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((g, gi) => (
                                    <React.Fragment key={gi}>
                                        <tr style={{ background: '#faf5e6' }}><td colSpan={6} style={{ padding: '6px 9px', fontWeight: 800, color: '#6b4f1c' }}>▸ {g.label} — {g.n} · {fmtEur(g.sum)}</td></tr>
                                        {g.sub.map((sg, si) => (
                                            <React.Fragment key={si}>
                                                {sg.label ? <tr style={{ background: '#fffdf7' }}><td colSpan={6} style={{ padding: '4px 9px 4px 20px', fontStyle: 'italic', color: '#8a6817', fontSize: '0.78rem' }}>{sg.label} ({sg.n})</td></tr> : null}
                                                {sg.rows.map((it, ii) => (
                                                    <tr key={ii} style={{ borderBottom: '1px solid #f3ecdb' }}>
                                                        <td style={{ padding: '5px 9px' }}>{it.public_id}</td>
                                                        <td style={{ padding: '5px 9px' }}>{it.branch}</td>
                                                        <td style={{ padding: '5px 9px' }}>{it.reason}</td>
                                                        <td style={{ padding: '5px 9px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtEur(it.amount)}</td>
                                                        <td style={{ padding: '5px 9px' }}>{it.status}</td>
                                                        <td style={{ padding: '5px 9px', color: '#8a7a55' }}>{it.order}</td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </Modal>
    );
}

export function PaymentTrafficModule({ token, locale, onBack }: { token: string | null; locale: Locale; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [state, setState] = useState<State | null>(null);
    const [rows, setRows] = useState<PaymentRequest[]>([]);
    const [accounts, setAccounts] = useState<BankAccount[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');       // debounced
    const [fromDate, setFromDate] = useState('');   // ISO yyyy-mm-dd
    const [toDate, setToDate] = useState('');
    useEffect(() => { const h = window.setTimeout(() => setSearch(searchInput), 350); return () => window.clearTimeout(h); }, [searchInput]);
    const [showForm, setShowForm] = useState(false);
    // Top-level upload: učitaj PDF bilo kad → parser detektira tip → otvori popunjeni zahtjev.
    const [preUpload, setPreUpload] = useState<{ parsed: ParsedDoc; file: File } | null>(null);
    const [uploadingTop, setUploadingTop] = useState(false);
    const handleTopUpload = async (file: File) => {
        setUploadingTop(true);
        try {
            const fd = new FormData();
            fd.append('document', file);
            const r = await fetch('/api/payment-traffic/parse-document', { method: 'POST', headers: { Authorization: 'Bearer ' + token, Accept: 'application/json' }, body: fd });
            const j = await r.json();
            if (!r.ok) throw new Error(j?.message || j?.error || `HTTP ${r.status}`);
            setPreUpload({ parsed: j as ParsedDoc, file });
            setShowForm(true);
        } catch (e) { alert('Ne mogu pročitati dokument: ' + (e instanceof Error ? e.message : String(e))); }
        finally { setUploadingTop(false); }
    };
    const [actionRow, setActionRow] = useState<PaymentRequest | null>(null);
    const [tab, setTab] = useState<PtTab>('requests');
    const [showTrash, setShowTrash] = useState(false);   // Koš (owner-only)

    const buildParams = useCallback(() => {
        const params = new URLSearchParams();
        if (showTrash) { params.set('deleted', '1'); return params; }   // koš → svi obrisani (bez ostalih filtera)
        if (filterStatus) params.set('status', filterStatus);
        if (search.trim()) params.set('search', search.trim());
        if (fromDate) params.set('from', fromDate);
        if (toDate) params.set('to', toDate);
        return params;
    }, [filterStatus, search, fromDate, toDate, showTrash]);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            const [s, r] = await Promise.all([
                api<State>('/payment-traffic/state', token),
                api<{ rows: PaymentRequest[] }>(`/payment-traffic/requests?${buildParams().toString()}`, token),
            ]);
            setState(s);
            setRows(r.rows ?? []);
        } catch { /* */ }
    }, [token, buildParams]);

    const isoOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const setQuickRange = (kind: 'today' | 'yesterday' | 'week' | 'all') => {
        const now = new Date();
        if (kind === 'all') { setFromDate(''); setToDate(''); return; }
        if (kind === 'today') { const iso = isoOf(now); setFromDate(iso); setToDate(iso); return; }
        if (kind === 'yesterday') { const y = new Date(now); y.setDate(now.getDate() - 1); const iso = isoOf(y); setFromDate(iso); setToDate(iso); return; }
        const w = new Date(now); w.setDate(now.getDate() - 6); setFromDate(isoOf(w)); setToDate(isoOf(now));
    };
    const doExport = async () => {
        try { await downloadFile(`/payment-traffic/requests-export?${buildParams().toString()}`, token, `platni_promet_${isoOf(new Date())}.xlsx`); }
        catch (e) { alert('Export greška: ' + (e instanceof Error ? e.message : String(e))); }
    };
    // Dnevni izvještaj → prvo overlay s podacima, pa odabir PDF/XLS.
    const [dailyOpen, setDailyOpen] = useState(false);
    const [dailyDate, setDailyDate] = useState('');
    const [dailyData, setDailyData] = useState<DailyReport | null>(null);
    const [dailyLoading, setDailyLoading] = useState(false);
    const loadDaily = useCallback(async (d: string) => {
        setDailyLoading(true); setDailyData(null);
        try { setDailyData(await api<DailyReport>(`/payment-traffic/daily-report/data?date=${d}`, token)); }
        catch (e) { alert('Izvještaj greška: ' + (e instanceof Error ? e.message : String(e))); }
        finally { setDailyLoading(false); }
    }, [token]);
    const openDailyReport = () => {
        const d = fromDate || isoOf(new Date());
        setDailyDate(d); setDailyOpen(true); void loadDaily(d);
    };
    const doDailyReport = openDailyReport;

    // UID 652: klik na "Dokument već korišten" badge → skoči na raniju pojavnost (očisti filtere/
    // datumski raspon da je sigurno vidljiva, prebaci na search). Operater vidi liniju dolje.
    const jumpToDuplicate = (publicId: string) => {
        setActionRow(null);
        setShowTrash(false);
        setFilterStatus('');
        setFromDate('');
        setToDate('');
        setSearchInput(publicId);
    };

    useEffect(() => { void load(); }, [load]);
    // Periodični refresh liste (safety net) — novi zahtjevi se pojave i bez ručnog reloada.
    useEffect(() => {
        const h = window.setInterval(() => void load(), 30000);
        const onFocus = () => void load();
        window.addEventListener('focus', onFocus);
        return () => { window.clearInterval(h); window.removeEventListener('focus', onFocus); };
    }, [load]);
    useEffect(() => {
        if (!token) return;
        void api<{ rows: BankAccount[] }>('/admin/bank-accounts?status=active', token).then((r) => setAccounts(r.rows ?? [])).catch(() => setAccounts([]));
        void api<{ branches: Branch[] }>('/payment-traffic/branches', token).then((r) => setBranches(r.branches ?? [])).catch(() => setBranches([]));
    }, [token]);

    const toggleMyMail = async (next: boolean) => {
        try {
            await mutate('/payment-traffic/me/notifications', token!, 'PATCH', { notify_payment_requests: next });
            setState((s) => s ? { ...s, notify_payment_requests: next } : s);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
    };

    if (!token) return null;

    // Postavke tab (admin/owner) — zaseban prikaz s vlastitim tab barom.
    if (tab === 'settings' && state?.is_admin_role) {
        return (
            <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
                <PtTabBar tab={tab} setTab={setTab} t={t} waiting={state?.waiting_for_me} showSettings={!!state?.is_admin_role} />
                <PaymentTrafficSettings token={token} locale={locale} />
            </div>
        );
    }

    // Raspored rada — dostupan svima s pristupom modulu (uređivanje gated u kontroleru/API-ju).
    if (tab === 'roster') {
        return (
            <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
                <PtTabBar tab={tab} setTab={setTab} t={t} waiting={state?.waiting_for_me} showSettings={!!state?.is_admin_role} />
                <PtDutyRosterView token={token} locale={locale} />
            </div>
        );
    }

    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    // ── HRMS modern path — Crumbs + Card-wrapped DataTable, full width ──
    const pillToneFor = (s: PaymentRequest['status']): 'ok' | 'warn' | 'bad' | 'info' | 'neutral' => (
        (s === 'received_branch' || s === 'zavrseno') ? 'ok'
            : (s === 'rejected' || s === 'problematicno') ? 'bad'
            : (s === 'pending' || s === 'ceka_voditelja' || s === 'ceka_posebno_odobrenje' || s === 'vraceno_na_doradu') ? 'warn'
            : (s === 'executed' || s === 'otkazano') ? 'neutral' : 'info'
    );
    const cols: Col<PaymentRequest>[] = [
        { key: 'public_id', label: t('Broj', 'ID'), render: (r) => (
            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: ui.ink }}>{r.public_id}</span>
                {duplicateBadge(r, locale, jumpToDuplicate)}
            </span>
        ) },
        { key: 'created_at', label: t('Kreirano', 'Created'), render: (r) => <span style={{ color: ui.muted }}>{fmtDateTime(r.created_at)}</span> },
        { key: 'branch', label: t('Poslovnica', 'Branch'), render: (r) => r.branch_name ?? '—' },
        { key: 'reason', label: t('Razlog', 'Reason'), render: (r) => { const m = REASON_META[r.reason_code]; return <span style={{ color: m.color }}>{m.icon} {locale === 'en' ? m.en : m.hr}</span>; } },
        { key: 'amount', label: t('Iznos', 'Amount'), align: 'right', render: (r) => <span style={{ fontWeight: 700, color: ui.ink }}>{fmtEur(r.amount_eur)}</span> },
        { key: 'target', label: t('Isplata na', 'Payout to'), render: (r) => payoutToCell(r, locale) },
        { key: 'match', label: t('Uparenost', 'Match'), render: (r) => matchBadge(r, locale) },
        { key: 'status', label: t('Status', 'Status'), render: (r) => <Pill tone={pillToneFor(r.status)}>{locale === 'en' ? STATUS_META[r.status].en : STATUS_META[r.status].hr}</Pill> },
        { key: 'act', label: '', align: 'right', render: (r) => <span onClick={(e) => e.stopPropagation()}><Btn size="sm" variant="ghost" onClick={() => setActionRow(r)}>{t('Otvori', 'Open')}</Btn></span> },
    ];

    const modern = (
        <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                <Crumbs items={[t('Dashboard', 'Dashboard'), t('Platni promet', 'Payment traffic')]} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <p style={{ margin: 0, fontSize: '0.86rem', color: ui.muted }}>
                        {t('Zahtjevi za uplate poslovnica → admin uplata. Zatvara potrebu za pozivima.', 'Branch payment requests → admin operations. Replaces the phone calls.')}
                    </p>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <PtNotifBell token={token} locale={locale} state={state} onActivity={() => void load()} />
                        {state?.is_admin ? (
                            <label title={t('Primaj mail kad poslovnica pošalje novi zahtjev', 'Get email when a branch sends a new request')}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: ui.ink2, cursor: 'pointer', padding: '6px 10px', background: ui.accSoft, border: `1px solid ${ui.line}`, borderRadius: 10 }}>
                                <input type="checkbox" checked={!!state.notify_payment_requests} onChange={(e) => void toggleMyMail(e.target.checked)} />
                                ✉ {t('Mail o novim zahtjevima', 'Email on new requests')}
                            </label>
                        ) : null}
                        {state ? (
                            <Pill tone={(state.pending_count ?? state.queue_count) > 0 ? 'warn' : 'ok'}>⏳ {state.pending_count ?? state.queue_count} {t('u redu', 'in queue')}</Pill>
                        ) : null}
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#6b4f1c', color: '#fff', padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: '0.86rem', cursor: uploadingTop ? 'wait' : 'pointer' }} title={t('Učitaj otkup/ponudu/račun — sam detektiram tip i otvorim popunjeni zahtjev', 'Load buyback/offer/invoice — auto-detects and opens a pre-filled request')}>
                            {uploadingTop ? '⏳ ' + t('Čitam…', 'Reading…') : '📄 ' + t('Učitaj PDF', 'Load PDF')}
                            <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={uploadingTop} onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleTopUpload(f); e.target.value = ''; }} />
                        </label>
                        <Btn variant="primary" onClick={() => setShowForm(true)}>＋ {t('Novi zahtjev', 'New request')}</Btn>
                    </div>
                </div>
            </div>

            <PtTabBar tab={tab} setTab={setTab} t={t} waiting={state?.waiting_for_me} showSettings={!!state?.is_admin_role} />

            {/* Status filter chips */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <Btn size="sm" variant={filterStatus === '' && !showTrash ? 'primary' : 'soft'} onClick={() => { setShowTrash(false); setFilterStatus(''); }}>{t('Sve', 'All')} ({showTrash ? 0 : rows.length})</Btn>
                {(Object.entries(STATUS_META) as Array<[PaymentRequest['status'], typeof STATUS_META.pending]>).map(([k, m]) => {
                    const count = showTrash ? 0 : rows.filter((r) => r.status === k).length;
                    return (
                        <Btn key={k} size="sm" variant={filterStatus === k && !showTrash ? 'primary' : 'soft'} onClick={() => { setShowTrash(false); setFilterStatus(k); }}>{locale === 'en' ? m.en : m.hr} ({count})</Btn>
                    );
                })}
                {state?.is_owner ? (
                    <button type="button" onClick={() => { setShowTrash((s) => !s); setFilterStatus(''); }}
                        style={{ marginLeft: 'auto', background: showTrash ? '#b14040' : '#fff', color: showTrash ? '#fff' : '#b14040', border: '1px solid #b14040', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' }}>
                        🗑 {t('Koš', 'Trash')} ({showTrash ? rows.length : (state?.deleted_count ?? 0)})
                    </button>
                ) : null}
            </div>

            {/* Filteri: quick search + brzi datumi + raspon + XLS export */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={'🔍 ' + t('Broj, poslovnica, podnositelj, otkup…', 'ID, branch, requester, buy no…')}
                    style={{ flex: '1 1 260px', minWidth: 200, padding: '7px 12px', border: `1px solid ${ui.line}`, borderRadius: 8, fontSize: '0.84rem' }} />
                <div style={{ display: 'flex', gap: 4 }}>
                    {(['today', 'yesterday', 'week', 'all'] as const).map((k) => (
                        <Btn key={k} size="sm" variant="soft" onClick={() => setQuickRange(k)}>{t({ today: 'Danas', yesterday: 'Jučer', week: 'Tjedan', all: 'Sve' }[k], { today: 'Today', yesterday: 'Yesterday', week: 'Week', all: 'All' }[k])}</Btn>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: '0.78rem', color: ui.muted }}>
                    {t('od', 'from')} <div style={{ width: 120 }}><DateInput value={fromDate} onChange={setFromDate} /></div>
                    {t('do', 'to')} <div style={{ width: 120 }}><DateInput value={toDate} onChange={setToDate} /></div>
                </div>
                <Btn size="sm" variant="soft" onClick={() => void doExport()}>⬇ {t('XLS export', 'XLS export')}</Btn>
                <Btn size="sm" variant="soft" onClick={() => void doDailyReport()}>📊 {t('Dnevni izvještaj', 'Daily report')}</Btn>
            </div>

            <Card pad={0}>
                <DataTable cols={cols} rows={rows} onRowClick={(r) => setActionRow(r)} empty={t('Nema zahtjeva za zadane filtre.', 'No requests match these filters.')} />
            </Card>

            {showForm ? <NewRequestModal token={token} locale={locale} state={state} accounts={accounts} branches={branches} preParsed={preUpload} onClose={(saved) => { setShowForm(false); setPreUpload(null); if (saved) void load(); }} /> : null}
            {actionRow ? <RequestActionsModal token={token} locale={locale} row={actionRow} isAdmin={state?.is_admin ?? false} state={state} accounts={accounts} onJumpToDuplicate={jumpToDuplicate} onClose={(reload) => { setActionRow(null); if (reload) void load(); }} /> : null}
            {dailyOpen ? <DailyReportModal token={token} locale={locale} date={dailyDate} data={dailyData} loading={dailyLoading} onDateChange={(d) => { setDailyDate(d); void loadDaily(d); }} onClose={() => setDailyOpen(false)} /> : null}
        </div>
    );

    if (isHrms) return modern;

    return (
        <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                    {onBack ? <button type="button" onClick={onBack} style={{ background: 'transparent', border: 0, color: 'var(--ad-ink)', cursor: 'pointer', fontSize: '0.86rem', padding: '4px 8px' }}>← {t('Natrag', 'Back')}</button> : null}
                    <h1 style={{ margin: '4px 0 0', color: 'var(--ad-ink)' }}>{t('21 · Platni promet', '21 · Payment traffic')}</h1>
                    <p className="muted" style={{ margin: '2px 0 0', fontSize: '0.86rem' }}>
                        {t('Zahtjevi za uplate poslovnica → admin uplata. Zatvara potrebu za pozivima.', 'Branch payment requests → admin operations. Replaces the phone calls.')}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <PtNotifBell token={token} locale={locale} state={state} onActivity={() => void load()} />
                    {state?.is_admin ? (
                        <label title={t('Primaj mail kad poslovnica pošalje novi zahtjev', 'Get email when a branch sends a new request')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--ad-ink)', cursor: 'pointer', padding: '4px 10px', background: '#fff7e0', border: '1px solid var(--ad-acc)', borderRadius: 6 }}>
                            <input type="checkbox" checked={!!state.notify_payment_requests} onChange={(e) => void toggleMyMail(e.target.checked)} />
                            ✉ {t('Mail o novim zahtjevima', 'Email on new requests')}
                        </label>
                    ) : null}
                    {state ? (
                        <div style={{ background: (state.pending_count ?? state.queue_count) > 0 ? 'var(--ad-acc)' : '#1f7a36', color: '#fff', padding: '4px 12px', borderRadius: 6, fontSize: '0.84rem', fontWeight: 700 }}>
                            ⏳ {state.pending_count ?? state.queue_count} {t('u redu', 'in queue')}
                        </div>
                    ) : null}
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#6b4f1c', color: '#fff', padding: '6px 14px', borderRadius: 6, fontWeight: 700, fontSize: '0.84rem', cursor: uploadingTop ? 'wait' : 'pointer' }} title={t('Učitaj otkup/ponudu/račun — sam detektiram tip i otvorim popunjeni zahtjev', 'Load buyback/offer/invoice — auto-detects and opens a pre-filled request')}>
                        {uploadingTop ? '⏳ ' + t('Čitam…', 'Reading…') : '📄 ' + t('Učitaj PDF', 'Load PDF')}
                        <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={uploadingTop} onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleTopUpload(f); e.target.value = ''; }} />
                    </label>
                    <button type="button" onClick={() => setShowForm(true)} style={{ background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer' }}>
                        + {t('Novi zahtjev', 'New request')}
                    </button>
                </div>
            </div>

            <PtTabBar tab={tab} setTab={setTab} t={t} waiting={state?.waiting_for_me} showSettings={!!state?.is_admin_role} />

            {/* Status filter chips */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setFilterStatus('')} style={{ padding: '4px 12px', background: filterStatus === '' ? 'var(--ad-ink)' : 'transparent', color: filterStatus === '' ? '#fff' : 'var(--ad-ink)', border: '1px solid var(--ad-acc)', borderRadius: 14, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 700 }}>
                    {t('Sve', 'All')} ({rows.length})
                </button>
                {(Object.entries(STATUS_META) as Array<[PaymentRequest['status'], typeof STATUS_META.pending]>).map(([k, m]) => {
                    const count = rows.filter((r) => r.status === k).length;
                    return (
                        <button key={k} type="button" onClick={() => setFilterStatus(k)} style={{ padding: '4px 12px', background: filterStatus === k ? m.bg : 'transparent', color: filterStatus === k ? m.color : m.bg, border: `1px solid ${m.bg}`, borderRadius: 14, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 700 }}>
                            {locale === 'en' ? m.en : m.hr} ({count})
                        </button>
                    );
                })}
            </div>

            {/* Filteri: quick search + brzi datumi + raspon + XLS export */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={'🔍 ' + t('Broj, poslovnica, podnositelj, otkup…', 'ID, branch, requester, buy no…')}
                    style={{ flex: '1 1 240px', minWidth: 180, padding: '6px 10px', border: '1px solid var(--ad-acc)', borderRadius: 8, fontSize: '0.82rem' }} />
                <div style={{ display: 'flex', gap: 4 }}>
                    {(['today', 'yesterday', 'week', 'all'] as const).map((k) => (
                        <button key={k} type="button" onClick={() => setQuickRange(k)} style={{ padding: '4px 10px', background: 'transparent', color: 'var(--ad-ink)', border: '1px solid var(--ad-acc)', borderRadius: 12, fontSize: '0.76rem', cursor: 'pointer', fontWeight: 700 }}>{t({ today: 'Danas', yesterday: 'Jučer', week: 'Tjedan', all: 'Sve' }[k], { today: 'Today', yesterday: 'Yesterday', week: 'Week', all: 'All' }[k])}</button>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: '0.76rem', color: '#888' }}>
                    {t('od', 'from')} <div style={{ width: 118 }}><DateInput value={fromDate} onChange={setFromDate} /></div>
                    {t('do', 'to')} <div style={{ width: 118 }}><DateInput value={toDate} onChange={setToDate} /></div>
                </div>
                <button type="button" onClick={() => void doExport()} style={{ padding: '5px 12px', background: 'var(--ad-ink)', color: '#fff', border: 0, borderRadius: 8, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 700 }}>⬇ {t('XLS export', 'XLS export')}</button>
                <button type="button" onClick={() => void doDailyReport()} style={{ padding: '5px 12px', background: 'var(--ad-acc)', color: '#fff', border: 0, borderRadius: 8, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 700 }}>📊 {t('Dnevni izvještaj', 'Daily report')}</button>
            </div>

            {rows.length === 0 ? <p className="muted" style={{ padding: 24, textAlign: 'center' }}>{t('Nema zahtjeva za zadane filtre.', 'No requests match these filters.')}</p>
            : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                        <tr style={{ background: '#fafaf6', color: 'var(--ad-ink)', textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px' }}>{t('Broj', 'ID')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Kreirano', 'Created')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Poslovnica', 'Branch')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Razlog', 'Reason')}</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Isplata na', 'Payout to')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Uparenost', 'Match')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Status', 'Status')}</th>
                            <th style={{ padding: '6px 8px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const reasonMeta = REASON_META[r.reason_code];
                            const statusMeta = STATUS_META[r.status];
                            return (
                                <tr key={r.id} style={{ borderTop: '1px solid #eee' }}>
                                    <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>
                                        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                                            <span>{r.public_id}</span>
                                            {duplicateBadge(r, locale, jumpToDuplicate)}
                                        </span>
                                    </td>
                                    <td style={{ padding: '5px 8px', color: '#888' }}>{fmtDateTime(r.created_at)}</td>
                                    <td style={{ padding: '5px 8px' }}>{r.branch_name ?? '—'}</td>
                                    <td style={{ padding: '5px 8px', color: reasonMeta.color }}>{reasonMeta.icon} {locale === 'en' ? reasonMeta.en : reasonMeta.hr}</td>
                                    <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700 }}>{fmtEur(r.amount_eur)}</td>
                                    <td style={{ padding: '5px 8px', fontSize: '0.74rem' }}>{payoutToCell(r, locale)}</td>
                                    <td style={{ padding: '5px 8px', fontSize: '0.74rem' }}>{matchBadge(r, locale)}</td>
                                    <td style={{ padding: '5px 8px' }}>
                                        <span style={{ background: statusMeta.bg, color: statusMeta.color, padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700 }}>
                                            {locale === 'en' ? statusMeta.en : statusMeta.hr}
                                        </span>
                                    </td>
                                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                                        <button type="button" onClick={() => setActionRow(r)} style={{ background: 'transparent', color: 'var(--ad-ink)', border: '1px solid var(--ad-acc)', borderRadius: 4, padding: '2px 8px', fontSize: '0.74rem', cursor: 'pointer' }}>
                                            {t('Otvori', 'Open')}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {showForm ? <NewRequestModal token={token} locale={locale} state={state} accounts={accounts} branches={branches} preParsed={preUpload} onClose={(saved) => { setShowForm(false); setPreUpload(null); if (saved) void load(); }} /> : null}
            {actionRow ? <RequestActionsModal token={token} locale={locale} row={actionRow} isAdmin={state?.is_admin ?? false} state={state} accounts={accounts} onJumpToDuplicate={jumpToDuplicate} onClose={(reload) => { setActionRow(null); if (reload) void load(); }} /> : null}
            {dailyOpen ? <DailyReportModal token={token} locale={locale} date={dailyDate} data={dailyData} loading={dailyLoading} onDateChange={(d) => { setDailyDate(d); void loadDaily(d); }} onClose={() => setDailyOpen(false)} /> : null}
        </div>
    );
}

function NewRequestModal({ token, locale, state, accounts, branches, preParsed, onClose }: { token: string; locale: Locale; state: State | null; accounts: BankAccount[]; branches: Branch[]; preParsed?: { parsed: ParsedDoc; file: File } | null; onClose: (saved: boolean) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const defaultBranch = (branches.length === 1 ? branches[0].id : null);
    const [form, setForm] = useState<{
        branch_dept_id: number | null;
        amount_eur: string;
        reason_code: ReasonCode;
        reason_notes: string;
        target_bank_account_id: number | null;
        // Virman fields (only when reason=virman_klijent)
        customer_name: string;
        customer_iban: string;
        customer_address: string;
        customer_city: string;
        payment_model: string;
        payment_reference: string;
        opis_placanja: string;
        source_subaccount_id: number | null;
        offer_order_ref: string;
        payout_company: 'ADMS' | 'ADO';
    }>({
        branch_dept_id: defaultBranch,
        amount_eur: '',
        reason_code: state?.pt_role === 'provjera_uplate' ? 'provjera_uplate' : 'dnevna_gotovina',
        reason_notes: '',
        target_bank_account_id: null,
        customer_name: '',
        customer_iban: '',
        customer_address: '',
        customer_city: '',
        payment_model: 'HR99',
        payment_reference: '',
        opis_placanja: '',
        source_subaccount_id: null,
        offer_order_ref: '',
        payout_company: 'ADMS',
    });
    const [proofFile, setProofFile] = useState<File | null>(null);
    const requiresBuybackPdf = form.reason_code === 'otkup_lom' || form.reason_code === 'otkup_invest';
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const fileRef = React.useRef<HTMLInputElement>(null);
    const [parsedDoc, setParsedDoc] = useState<ParsedDoc | null>(null);
    const [pdfParsed, setPdfParsed] = useState(false);   // je li nalog dignut iz PDF-a (za data_source)
    const [parsing, setParsing] = useState(false);
    // Ručni override odredišta isplate (operater ispravlja krivo prepoznat način). '' = koristi parse.
    const [payoutOverride, setPayoutOverride] = useState<'' | 'client' | 'branch'>('');

    // Ako user pripada samo jednoj poslovnici → auto-odaberi ju (branches stižu async pa useState init ne uhvati).
    useEffect(() => {
        if (branches.length === 1) setForm((f) => (f.branch_dept_id ? f : { ...f, branch_dept_id: branches[0].id }));
    }, [branches]);

    // Primijeni parsiran dokument na formu (koristi i upload unutar modala i top-level upload s liste).
    const applyParsed = (p: ParsedDoc, file: File) => {
        setParsedDoc(p);
        setPayoutOverride('');   // reset override na svaki novi dokument
        setPdfParsed(true);   // podaci dignuti iz dokumenta → 'ispravan nalog'
        setProofFile(file);   // isti PDF ide kao dokaz uz zahtjev
        setForm((f) => {
            const matchOk = p.matched_branch_id && branches.some((b) => b.id === p.matched_branch_id);
            const branchId = matchOk ? p.matched_branch_id : (f.branch_dept_id ?? (branches.length === 1 ? branches[0].id : null));
            const keepInflow = INFLOW_REASONS.includes(f.reason_code);
            const next = {
                ...f,
                reason_code: keepInflow ? f.reason_code : (p.reason_code ?? f.reason_code),
                amount_eur: p.amount_eur != null ? String(p.amount_eur) : f.amount_eur,
                customer_name: p.customer_name ?? f.customer_name,
                offer_order_ref: p.offer_order_ref ?? f.offer_order_ref,
                branch_dept_id: branchId,
            };
            // Gotovinski (lom) → isplata na podračun poslovnice: auto-odaberi ako poslovnica ima točno jedan.
            if (p.pays_to === 'branch_subaccount' && branchId) {
                const subs = accounts.filter((a) => a.branch_dept_id === branchId);
                if (subs.length === 1) next.target_bank_account_id = subs[0].id;
            }
            return next;
        });
    };

    // Top-level upload s liste: dokument je već parsiran → popuni odmah na otvaranju modala.
    useEffect(() => {
        if (preParsed) applyParsed(preParsed.parsed, preParsed.file);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Učitaj „Tržni nakup/otkup"/ponudu/račun PDF → auto-popuni polja + priloži kao dokaz.
    const uploadDoc = async (file: File) => {
        setParsing(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('document', file);
            const r = await fetch('/api/payment-traffic/parse-document', {
                method: 'POST', headers: { Authorization: 'Bearer ' + token, Accept: 'application/json' }, body: fd,
            });
            const j = await r.json();
            if (!r.ok) throw new Error(j?.message || j?.error || `HTTP ${r.status}`);
            applyParsed(j as ParsedDoc, file);
        } catch (e) { setErr(e instanceof Error ? e.message : 'Greška pri čitanju dokumenta'); setParsedDoc(null); }
        finally { setParsing(false); }
    };

    // Podračun strict-filter: samo računi vezani na odabranu poslovnicu (main glavni
    // računi se ne pojavljuju ovdje — to nisu podračuni nego naši glavni).
    const availableAccounts = useMemo(() => {
        if (!form.branch_dept_id) return [];
        return accounts.filter((a) => a.branch_dept_id === form.branch_dept_id);
    }, [accounts, form.branch_dept_id]);

    // P2: live preview isplate (zaokruženi iznos + banka klijenta + odabrani isplatni račun), debounced.
    const [preview, setPreview] = useState<PayoutPreview | null>(null);
    useEffect(() => {
        const amt = parseFloat(form.amount_eur || '0');
        // Priljev (provjera/upit o uplati) — nema isplate ni preview-a.
        if (!(amt > 0) || INFLOW_REASONS.includes(form.reason_code)) { setPreview(null); return; }
        const ctrl = new AbortController();
        const h = window.setTimeout(() => {
            const p = new URLSearchParams({ amount_eur: String(amt), reason_code: form.reason_code });
            if (form.branch_dept_id) p.set('branch_dept_id', String(form.branch_dept_id));
            // Banka klijenta određuje s kojeg NAŠEG računa iste banke isplaćujemo (bez naknade) — vrijedi za virman I otkup.
            if (form.customer_iban) p.set('client_iban', form.customer_iban);
            if (form.source_subaccount_id) p.set('source_subaccount_id', String(form.source_subaccount_id));
            // Efektivni način (override > parse) → točan iznos za klijentov račun, zaokruženo za podračun.
            const effNacin = payoutOverride === 'client' ? 'transakcijski' : payoutOverride === 'branch' ? 'gotovinski' : (parsedDoc?.nacin_isplate ?? '');
            if (effNacin) p.set('nacin_isplate', effNacin);
            fetch(`/api/payment-traffic/payout-preview?${p.toString()}`, { headers: { Authorization: 'Bearer ' + token, Accept: 'application/json' }, signal: ctrl.signal })
                .then((r) => (r.ok ? r.json() : null)).then((j) => setPreview(j)).catch(() => { /* aborted */ });
        }, 350);
        return () => { window.clearTimeout(h); ctrl.abort(); };
    }, [form.amount_eur, form.reason_code, form.customer_iban, form.source_subaccount_id, payoutOverride, parsedDoc?.nacin_isplate, token]);

    const save = async () => {
        setSaving(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('branch_dept_id', String(form.branch_dept_id));
            fd.append('amount_eur', form.amount_eur);
            fd.append('reason_code', form.reason_code);
            fd.append('data_source', pdfParsed ? 'pdf' : 'manual');   // ispravan (iz PDF-a) vs ručni nalog
            if (form.reason_notes) fd.append('reason_notes', form.reason_notes);
            // Isplata na račun klijenta (override 'client' ili unesen IBAN) NE smije nositi
            // target_bank_account_id — inače backend virman() ode na podračun poslovnice umjesto
            // na klijentov IBAN. Auto-postavljen target iz PDF parsea se ovdje izostavlja.
            const payingClient = !INFLOW_REASONS.includes(form.reason_code)
                && (form.reason_code === 'otkup_lom' || form.reason_code === 'otkup_invest' || payoutOverride === 'client' || !!form.customer_iban.trim());
            if (form.target_bank_account_id && !payingClient) fd.append('target_bank_account_id', String(form.target_bank_account_id));
            if (form.source_subaccount_id) fd.append('source_subaccount_id', String(form.source_subaccount_id));
            // Dnevna gotovina: poslovnica bira firmu (ADMS ili ADO). Ostali tipovi je izvode iz reason_code.
            if (form.reason_code === 'dnevna_gotovina') fd.append('payout_company', form.payout_company);
            // Virman fields — samo za reason_code=virman_klijent
            if (form.reason_code === 'virman_klijent') {
                if (form.customer_name) fd.append('customer_name', form.customer_name);
                if (form.customer_iban) fd.append('customer_iban', form.customer_iban);
                if (form.customer_address) fd.append('customer_address', form.customer_address);
                if (form.customer_city) fd.append('customer_city', form.customer_city);
                if (form.payment_model) fd.append('payment_model', form.payment_model);
                if (form.payment_reference) fd.append('payment_reference', form.payment_reference);
                if (form.opis_placanja) fd.append('opis_placanja', form.opis_placanja);
            }
            // Provjera uplate / upit o uplati (priljev) — ime klijenta (enkriptira se) + broj narudžbe
            if (INFLOW_REASONS.includes(form.reason_code)) {
                if (form.customer_name) fd.append('customer_name', form.customer_name);
                if (form.offer_order_ref) fd.append('offer_order_ref', form.offer_order_ref);
            }
            // Otkup (iz dokumenta) — ime kupca + IBAN (transakcijski) + broj otkupa
            if (form.reason_code === 'otkup_lom' || form.reason_code === 'otkup_invest') {
                if (form.customer_name) fd.append('customer_name', form.customer_name);
                if (form.customer_iban) fd.append('customer_iban', form.customer_iban);
                if (form.offer_order_ref) fd.append('offer_order_ref', form.offer_order_ref);
            }
            // Način isplate: operaterov override ima prednost nad parseom. transakcijski=točno (IBAN klijenta),
            // gotovinski=zaokruženo gore (podračun poslovnice).
            const effNacin = INFLOW_REASONS.includes(form.reason_code) ? null
                : requiresBuybackPdf ? 'transakcijski' : parsedDoc?.pays_to === 'inflow' ? null
                : (payoutOverride === 'client' ? 'transakcijski' : payoutOverride === 'branch' ? 'gotovinski' : (parsedDoc?.nacin_isplate ?? null));
            if (effNacin) fd.append('nacin_isplate', effNacin);
            if (proofFile) fd.append('proof_document', proofFile);

            const r = await fetch('/api/payment-traffic/requests', {
                method: 'POST',
                headers: { 'Authorization': 'Bearer '+token, 'Accept': 'application/json' },
                body: fd,
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) {
                throw new Error(j?.message || j?.error || `HTTP ${r.status}`);
            }
            // UID 652: dokument je bio već jednom u sustavu — ne blokira, ali operater MORA vidjeti
            // ovo ODMAH, ne tiho kroz badge na listi koji možda ni ne pogleda.
            if (j?.duplicate_of_public_id) {
                alert(t(
                    `Upozorenje: ovaj dokument je već ranije unesen kao ${j.duplicate_of_public_id}. Zahtjev ${j.public_id} je ipak kreiran — provjerite da ne dolazi do dvostruke isplate.`,
                    `Warning: this document was already entered before as ${j.duplicate_of_public_id}. Request ${j.public_id} was created anyway — check this does not cause a double payout.`
                ));
            }
            onClose(true);
        } catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setSaving(false); }
    };

    // Poslovnica dokumenta vs korisnikova — branch user ne smije poslati zahtjev za tuđu poslovnicu.
    // Dijakritika se skida prije usporedbe: „Trgovačka" (naziv u bazi) i „Trgovacka" (PDF tekst)
    // inače ne daju isti token, guard lažno okine i blokira podnošenje (slučaj Umag).
    const foldDia = (s: string) => (s || '').toLowerCase().replace(/[čć]/g, 'c').replace(/ž/g, 'z').replace(/š/g, 's').replace(/đ/g, 'd');
    const branchTok = (s: string) => foldDia(s).split(/[\s\-,.()]+/).filter((w) => w.length >= 3 && !['tbog', 'cco', 'west', 'east', 'trg', 'ulica', 'shop', 'moneyshop', 'poslovnica', 'zlatarna', 'nova', 'bank', 'gold', 'the'].includes(w));
    const docBranchTokens = branchTok(parsedDoc?.poslovnica_text ?? '');
    const userMatchesDocBranch = branches.some((b) => { const bt = new Set([...branchTok(b.name), ...branchTok(b.code ?? '')]); return docBranchTokens.some((tk) => bt.has(tk)); });
    const branchMismatch = !state?.is_manager && branches.length > 0 && !!parsedDoc?.poslovnica_text && docBranchTokens.length > 0 && !userMatchesDocBranch;

    // Tile je izvor istine za banner/IBAN: operater na Provjeri uplate ne vidi otkup+IBAN
    // čak i ako je parser (sken) krivo rekao otkup_invest (uid 240 Pula).
    const isInflowUi = INFLOW_REASONS.includes(form.reason_code);
    const isOtkupPayout = !isInflowUi && !requiresBuybackPdf && !!parsedDoc && parsedDoc.ok && parsedDoc.pays_to !== 'inflow';
    const effNeedsIban = isInflowUi ? false : requiresBuybackPdf ? true : parsedDoc?.pays_to === 'inflow' ? false
        : (payoutOverride === 'client' ? true : payoutOverride === 'branch' ? false : !!parsedDoc?.needs_client_iban);
    const hasRequiredBuybackPdf = !requiresBuybackPdf || (proofFile?.type === 'application/pdf' || proofFile?.name.toLowerCase().endsWith('.pdf'));
    const canSubmit = form.branch_dept_id && parseFloat(form.amount_eur || '0') > 0 && !saving
        && (!effNeedsIban || !!form.customer_iban.trim())
        && hasRequiredBuybackPdf
        && !branchMismatch;

    return (
        <Modal onClose={() => onClose(false)} wide>
            {/* Header */}
            <div style={{ borderBottom: '2px solid #f0ead7', paddingBottom: 12, marginBottom: 18 }}>
                <h2 style={{ margin: 0, color: 'var(--ad-ink)', fontSize: '1.3rem' }}>📨 {t('Novi zahtjev za uplatu', 'New payment request')}</h2>
                <p style={{ margin: '4px 0 0', fontSize: '0.86rem', color: '#888' }}>
                    {t('Slavica, Filip i Manuela vide zahtjev odmah i odobravaju ga čim mogu.', 'Slavica, Filip and Manuela see this immediately and approve as soon as possible.')}
                </p>
            </div>

            {/* 📄 Učitaj otkup dokument → auto-fill */}
            <div style={{ marginBottom: 18, padding: 14, borderRadius: 10, border: '2px dashed #c9b98a', background: '#fdfaf0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ fontWeight: 800, color: '#6b4f1c' }}>📄 {t('Učitaj dokument (PDF)', 'Load document (PDF)')}</div>
                        <div style={{ fontSize: '0.76rem', color: '#8a7a55' }}>{t('Otkup (Tržni nakup) ILI prodaja/predujam → auto prepozna tip. Otkup: transakcijski → IBAN klijenta, gotovinski → podračun. Prodaja → provjera uplate.', 'Buyback OR sale/advance → auto-detects type. Buyback: transfer → client IBAN, cash → sub-account. Sale → payment check.')}</div>
                    </div>
                    <label style={{ background: '#6b4f1c', color: '#fff', padding: '8px 16px', borderRadius: 8, cursor: parsing ? 'wait' : 'pointer', fontWeight: 700, fontSize: '0.86rem', whiteSpace: 'nowrap' }}>
                        {parsing ? '⏳ ' + t('Čitam…', 'Reading…') : '📎 ' + t('Odaberi PDF', 'Choose PDF')}
                        <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={parsing} onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadDoc(f); e.target.value = ''; }} />
                    </label>
                </div>
                {parsedDoc && parsedDoc.ok ? (
                    <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: '#eef7f0', border: '1px solid #cfe6d6', fontSize: '0.82rem' }}>
                        <div style={{ fontWeight: 700, color: isInflowUi ? '#1e40af' : '#1f7a36' }}>✅ {t('Prepoznato', 'Detected')}: {REASON_META[form.reason_code]?.hr ?? (parsedDoc.reason_code ? REASON_META[parsedDoc.reason_code].hr : '—')}{!isInflowUi && parsedDoc.nacin_isplate ? ' · ' + parsedDoc.nacin_isplate : ''}</div>
                        <div style={{ color: '#444', marginTop: 2 }}>
                            {parsedDoc.customer_name ? '👤 ' + parsedDoc.customer_name + ' · ' : ''}
                            {parsedDoc.amount_eur != null ? '💶 ' + fmtEur(parsedDoc.amount_eur) + ' · ' : ''}
                            {parsedDoc.offer_order_ref ? '#' + parsedDoc.offer_order_ref + ' · ' : ''}
                            {parsedDoc.poslovnica_text ? '🏬 ' + parsedDoc.poslovnica_text : ''}
                        </div>
                        <div style={{ marginTop: 4, fontWeight: 700, color: isInflowUi || parsedDoc.pays_to === 'inflow' ? '#1e40af' : (effNeedsIban ? '#b14040' : '#1f7a36') }}>
                            {isInflowUi || parsedDoc.pays_to === 'inflow'
                                ? '💰 ' + t('Provjera uplate — klijent plaća nama (nije isplata)', 'Payment check — client pays us (not a payout)')
                                : effNeedsIban
                                    ? '📨 ' + t('Isplata na račun KLIJENTA — unesi IBAN', 'Payout to CLIENT account — enter IBAN')
                                    : '🏦 ' + t('Isplata na podračun poslovnice', 'Payout to branch sub-account')}
                        </div>
                        {/* Ručni override odredišta — operater ispravlja krivo prepoznat način (npr. ADO otkup transakcijski). */}
                        {isOtkupPayout ? (
                            <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.76rem', color: '#666' }}>{t('Isplata na:', 'Payout to:')}</span>
                                <button type="button" onClick={() => setPayoutOverride('branch')} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #cfe6d6', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, background: !effNeedsIban ? '#1f7a36' : '#fff', color: !effNeedsIban ? '#fff' : '#444' }}>🏦 {t('Podračun poslovnice', 'Branch sub-account')}</button>
                                <button type="button" onClick={() => setPayoutOverride('client')} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e6b0b0', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, background: effNeedsIban ? '#b14040' : '#fff', color: effNeedsIban ? '#fff' : '#444' }}>📨 {t('Račun klijenta (IBAN)', 'Client account (IBAN)')}</button>
                                {payoutOverride ? <span style={{ fontSize: '0.72rem', color: '#c2701a' }}>✎ {t('ručno', 'manual')}</span> : null}
                            </div>
                        ) : null}
                        {branchMismatch ? (
                            <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: '#fbecec', border: '2px solid #d88', color: '#b14040', fontWeight: 700 }}>
                                ⛔ {t('Dokument je za poslovnicu', 'Document is for branch')} «{parsedDoc.poslovnica_text}», {t('a ti pripadaš', 'but you belong to')} {branches.map((b) => b.name).join(', ')}.
                                <div style={{ fontWeight: 400, marginTop: 2 }}>{t('Ne možeš dovršiti ovaj zahtjev — proslijedi ga svojoj poslovnici.', 'You cannot submit this request — forward it to its own branch.')}</div>
                            </div>
                        ) : null}
                        {parsedDoc.poslovnica_text && !parsedDoc.matched_branch_id ? <div style={{ color: '#c2701a', marginTop: 2 }}>⚠ {t('Poslovnica nije automatski prepoznata — odaberi ju ručno dolje.', 'Branch not auto-matched — pick it below.')}</div> : null}
                        {effNeedsIban ? (
                            <div style={{ marginTop: 8 }}>
                                <label style={{ display: 'block', fontSize: '0.78rem', color: '#b14040', fontWeight: 700, marginBottom: 4 }}>{t('IBAN klijenta (obavezno)', 'Client IBAN (required)')}</label>
                                <input type="text" value={form.customer_iban} onChange={(e) => setForm({ ...form, customer_iban: e.target.value.toUpperCase() })} placeholder="HR.." style={{ width: '100%', padding: '8px 12px', border: '2px solid #e6b0b0', borderRadius: 6, fontFamily: 'ui-monospace, monospace', boxSizing: 'border-box' }} />
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {/* Razlog — tile picker */}
            <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
                    {t('1 · Razlog uplate', '1 · Reason')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${state?.pt_role === 'provjera_uplate' ? 1 : 5}, 1fr)`, gap: 8 }}>
                    {(state?.pt_role === 'provjera_uplate' ? (['provjera_uplate'] as ReasonCode[]) : REASON_SELECTABLE).map((k) => {
                        const m = REASON_META[k];
                        const active = form.reason_code === k;
                        return (
                            <button key={k} type="button" onClick={() => {
                                if (INFLOW_REASONS.includes(k)) setPayoutOverride('');
                                setForm({
                                    ...form,
                                    reason_code: k,
                                    target_bank_account_id: INFLOW_REASONS.includes(k) ? null : form.target_bank_account_id,
                                    customer_iban: INFLOW_REASONS.includes(k) ? '' : form.customer_iban,
                                });
                            }}
                                style={{ background: active ? m.color : '#fff', color: active ? '#fff' : m.color, border: `2px solid ${m.color}`, borderRadius: 8, padding: '12px 8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, transition: 'all 100ms ease' }}>
                                <div style={{ fontSize: '1.4rem' }}>{m.icon}</div>
                                {locale === 'en' ? m.en : m.hr}
                            </button>
                        );
                    })}
                </div>
                {/* Dnevna gotovina — odabir firme (ADMS / ADO). ADO više ne mora ići kroz „otkup investicijskog". */}
                {form.reason_code === 'dnevna_gotovina' ? (
                    <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.76rem', color: '#666', fontWeight: 700 }}>{t('Firma:', 'Company:')}</span>
                        {(['ADMS', 'ADO'] as const).map((c) => (
                            <button key={c} type="button" onClick={() => setForm({ ...form, payout_company: c })}
                                style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #c9b98a', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, background: form.payout_company === c ? '#6b4f1c' : '#fff', color: form.payout_company === c ? '#fff' : '#444' }}>{c}</button>
                        ))}
                    </div>
                ) : null}
            </div>

            {/* Poslovnica + iznos */}
            <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
                    {t('2 · Poslovnica i iznos', '2 · Branch and amount')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Poslovnica', 'Branch')}</label>
                        <select value={form.branch_dept_id ?? ''} onChange={(e) => setForm({ ...form, branch_dept_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                            style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--ad-acc)', borderRadius: 6, fontSize: '0.96rem', background: '#fff' }}>
                            <option value="">{t('— odaberi poslovnicu —', '— pick branch —')}</option>
                            {branches.map((b) => <option key={b.id} value={b.id}>{b.code ? b.code + ' · ' : ''}{b.name}</option>)}
                        </select>
                        {branches.length === 0 ? <div style={{ fontSize: '0.74rem', color: '#b14040', marginTop: 4 }}>⚠ {t('Nemaš dodjeljenu poslovnicu. Kontaktiraj administratora.', 'No branch assigned. Contact admin.')}</div> : null}
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Iznos', 'Amount')}</label>
                        <div style={{ position: 'relative' }}>
                            <input type="number" step="0.01" min="0.01" value={form.amount_eur} onChange={(e) => setForm({ ...form, amount_eur: e.target.value })}
                                placeholder="0,00"
                                style={{ width: '100%', padding: '10px 36px 10px 12px', border: '1px solid var(--ad-acc)', borderRadius: 6, fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: '1.1rem', textAlign: 'right', background: '#fff' }} />
                            <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#888', fontWeight: 700 }}>€</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* P2: live preview isplate (iznos zaokružen + odabrani isplatni račun) */}
            {preview ? (
                <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: '#f3f8f4', border: '1px solid #cfe6d6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                            <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>{t('Iznos isplate', 'Payout amount')}</div>
                            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1f7a36', fontFamily: 'ui-monospace, monospace' }}>{fmtEur(preview.payout_amount_eur)}</div>
                            {preview.rounded_up ? <div style={{ fontSize: '0.72rem', color: '#c2701a' }}>↑ {t('zaokruženo na desetice s', 'rounded up from')} {fmtEur(preview.amount_eur)}</div> : null}
                        </div>
                        <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#444' }}>
                            {preview.payout_account.account_id ? (
                                <>
                                    <div>{t('Isplata s:', 'Pay from:')} <strong>{preview.payout_account.bank_short ?? preview.payout_account.bank_name ?? '—'}</strong> · <code>…{(preview.payout_account.iban ?? '').slice(-6)}</code></div>
                                    <div style={{ fontSize: '0.72rem', color: '#888' }}>{(PAYOUT_RESOLUTION_LABEL[preview.payout_account.resolution] ?? [preview.payout_account.resolution, ''])[locale === 'en' ? 1 : 0]}</div>
                                </>
                            ) : <div style={{ color: '#b14040' }}>{t('Isplatni račun nije određen — admin bira.', 'Payout account undetermined — admin picks.')}</div>}
                            {preview.client_bank.short ? <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 2 }}>{t('Banka klijenta:', 'Client bank:')} {preview.client_bank.short}{preview.client_iban_valid === false ? <span style={{ color: '#b14040' }}> · ⚠ {t('IBAN neispravan', 'invalid IBAN')}</span> : null}</div> : null}
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Ciljani podračun + bilješke */}
            <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
                    {t('3 · Detalji (opcionalno)', '3 · Details (optional)')}
                </div>
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Ciljani podračun (poslovnice)', 'Target sub-account (branch)')}</label>
                <select value={form.target_bank_account_id ?? ''} onChange={(e) => setForm({ ...form, target_bank_account_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                    disabled={!form.branch_dept_id}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.92rem', background: form.branch_dept_id ? '#fff' : '#f5f5f5', marginBottom: 4 }}>
                    <option value="">
                        {!form.branch_dept_id ? t('Odaberi poslovnicu prvo', 'Pick branch first')
                        : availableAccounts.length === 0 ? t('— nema podračuna, admin će odlučiti —', '— no sub-accounts, admin picks —')
                        : t('— admin će odlučiti —', '— admin picks —')}
                    </option>
                    {availableAccounts.map((a) => <option key={a.id} value={a.id}>{a.bank_name} · {a.account_label} · …{a.iban.slice(-6)}</option>)}
                </select>
                {form.branch_dept_id && availableAccounts.length === 0 ? (
                    <div style={{ fontSize: '0.72rem', color: 'var(--ad-acc)', marginBottom: 10 }}>
                        ℹ {t('Ova poslovnica još nema dodjeljene podračune u m.132. Admin uplate će odrediti pri obradi.', 'This branch has no sub-accounts assigned in m.132. Payment admin will pick at processing.')}
                    </div>
                ) : <div style={{ marginBottom: 6 }} />}
                <label style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Bilješke', 'Notes')}</label>
                <textarea value={form.reason_notes} onChange={(e) => setForm({ ...form, reason_notes: e.target.value })} rows={2}
                    placeholder={t('npr. Hitno za 14h, klijent već u poslovnici', 'e.g. urgent by 2pm, customer in store')}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>

            {/* Provjera uplate (priljev) — učitaj prodajni dokument → iznos + broj narudžbe + ime klijenta */}
            {form.reason_code === 'provjera_uplate' ? (
                <div style={{ marginBottom: 18, background: 'rgba(30,64,175,0.05)', border: '1px solid rgba(30,64,175,0.3)', borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: '0.74rem', color: '#1e40af', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        💰 {t('Provjera uplate — je li vidljiva?', 'Payment check — is it visible?')}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: '#666', marginBottom: 10 }}>
                        {t('Učitaj prodajni dokument (prodaja investicijskog) gore → iznos, broj narudžbe i ime klijenta se popune. Računovodstvo kad vidi uplatu klikne „Uplata je vidljiva". Nije isplata.', 'Load the sales document above → amount, order no. and client name fill in. Accounting confirms "Payment visible". Not a payout.')}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <label>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Ime klijenta', 'Client name')}</span>
                            <input type="text" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.92rem', boxSizing: 'border-box' }} />
                        </label>
                        <label>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Broj narudžbe', 'Order no.')}</span>
                            <input type="text" value={form.offer_order_ref} onChange={(e) => setForm({ ...form, offer_order_ref: e.target.value })}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.92rem', boxSizing: 'border-box' }} />
                        </label>
                    </div>
                </div>
            ) : null}

            {form.reason_code === 'virman_klijent' ? (
                <div style={{ marginBottom: 18, background: 'rgba(177,64,64,0.05)', border: '1px solid rgba(177,64,64,0.3)', borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: '0.74rem', color: '#b14040', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        🔒 {t('Virman — podaci klijenta (enkriptirano)', 'Virman — customer details (encrypted)')}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: '#666', marginBottom: 10 }}>
                        {t('IBAN i ime se odmah enkriptiraju. Admin uplate (Slavica/Filip/Manuela) ih može otkriti, svaki klik se loga.', 'IBAN and name are encrypted immediately. Admins can reveal them, every click is audited.')}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <label style={{ gridColumn: 'span 2' }}>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Ime i prezime klijenta', 'Customer name')}</span>
                            <input type="text" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.92rem' }} />
                        </label>
                        <label style={{ gridColumn: 'span 2' }}>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('IBAN klijenta', 'Customer IBAN')}</span>
                            <input type="text" value={form.customer_iban} onChange={(e) => setForm({ ...form, customer_iban: e.target.value.toUpperCase() })}
                                placeholder="HR1234567890123456789"
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontFamily: 'ui-monospace, monospace', fontSize: '0.92rem' }} />
                        </label>
                        <label>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Adresa (opc.)', 'Address (opt)')}</span>
                            <input type="text" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.92rem' }} />
                        </label>
                        <label>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Grad (opc.)', 'City (opt)')}</span>
                            <input type="text" value={form.customer_city} onChange={(e) => setForm({ ...form, customer_city: e.target.value })}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.92rem' }} />
                        </label>
                        <label>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Model', 'Model')}</span>
                            <input type="text" value={form.payment_model} onChange={(e) => setForm({ ...form, payment_model: e.target.value.toUpperCase() })}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontFamily: 'ui-monospace, monospace', fontSize: '0.92rem' }} />
                        </label>
                        <label>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Poziv na broj', 'Reference')}</span>
                            <input type="text" value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontFamily: 'ui-monospace, monospace', fontSize: '0.92rem' }} />
                        </label>
                        <label style={{ gridColumn: 'span 2' }}>
                            <span style={{ display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: 4 }}>{t('Opis plaćanja', 'Payment description')}</span>
                            <input type="text" value={form.opis_placanja} onChange={(e) => setForm({ ...form, opis_placanja: e.target.value })}
                                placeholder={t('npr. Otkup loma 245g', 'e.g. Scrap buyback 245g')}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.92rem' }} />
                        </label>
                    </div>
                </div>
            ) : null}

            {/* PDF upload (kontrolna ponuda) */}
            <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: '0.74rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
                    {requiresBuybackPdf
                        ? t('4 · PDF otkupa (obavezno)', '4 · Buyback PDF (required)')
                        : t('4 · Kontrolni dokument (opcionalno)', '4 · Control document (optional)')}
                </div>
                <input ref={fileRef} type="file" accept={requiresBuybackPdf ? 'application/pdf' : 'application/pdf,image/png,image/jpeg'} onChange={(e) => setProofFile(e.target.files?.[0] ?? null)} style={{ display: 'none' }} />
                {!proofFile ? (
                    <button type="button" onClick={() => fileRef.current?.click()}
                        style={{ width: '100%', padding: '16px', border: '2px dashed var(--ad-acc)', borderRadius: 8, background: '#fafaf6', cursor: 'pointer', color: 'var(--ad-ink)', fontSize: '0.86rem', fontWeight: 600, textAlign: 'center' }}>
                        📎 {t('Klikni za upload PDF-a ponude (kontrolni doc)', 'Click to upload PDF (control doc)')}
                        <div style={{ fontSize: '0.74rem', color: '#888', marginTop: 4, fontWeight: 400 }}>
                            {requiresBuybackPdf
                                ? t('Za Otkup loma i Otkup investicijskog zahtjev se ne može poslati bez PDF-a otkupa.', 'Scrap and investment buyback requests cannot be submitted without the buyback PDF.')
                                : t('Iz njega ćemo kasnije auto-detektirati iznos i poslovnicu.', 'We will later auto-detect amount and branch from it.')}
                        </div>
                    </button>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: '1px solid #1f7a36', borderRadius: 6, background: 'rgba(31,122,54,0.06)' }}>
                        <span style={{ fontSize: '1.2rem' }}>📄</span>
                        <span style={{ flex: 1, fontWeight: 600, color: '#1f7a36' }}>{proofFile.name}</span>
                        <span style={{ fontSize: '0.74rem', color: '#888' }}>{(proofFile.size / 1024).toFixed(0)} kB</span>
                        <button type="button" onClick={() => { setProofFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                            style={{ background: 'transparent', border: 0, color: '#b14040', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
                    </div>
                )}
            </div>

            {err ? <div style={{ background: 'rgba(177,64,64,0.1)', color: '#b14040', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: '0.86rem' }}>⚠ {err}</div> : null}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #f0ead7', paddingTop: 14 }}>
                <button type="button" onClick={() => onClose(false)}
                    style={{ background: 'transparent', border: '1px solid #888', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', color: '#666', fontWeight: 600 }}>
                    {t('Odustani', 'Cancel')}
                </button>
                <button type="button" disabled={!canSubmit} onClick={() => void save()}
                    style={{ background: canSubmit ? '#1f7a36' : '#ccc', color: '#fff', border: 0, borderRadius: 6, padding: '8px 22px', cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: '0.96rem' }}>
                    {saving ? t('🔄 Šaljem…', '🔄 Sending…') : t('📤 Pošalji zahtjev', '📤 Send request')}
                </button>
            </div>
        </Modal>
    );
}

function RequestActionsModal({ token, locale, row, isAdmin, state, accounts, onJumpToDuplicate, onClose }: { token: string; locale: Locale; row: PaymentRequest; isAdmin: boolean; state: State | null; accounts: BankAccount[]; onJumpToDuplicate?: (publicId: string) => void; onClose: (reload: boolean) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [risRef, setRisRef] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [decisionNote, setDecisionNote] = useState('');
    const [resubmitAmount, setResubmitAmount] = useState(String(row.amount_eur ?? ''));
    const [resubmitNotes, setResubmitNotes] = useState(row.reason_notes ?? '');
    const [finalNote, setFinalNote] = useState('');
    const [cancelNote, setCancelNote] = useState('');
    const [cancelReason, setCancelReason] = useState('');
    const [history, setHistory] = useState<{ events: any[]; approvals: any[] } | null>(null);
    const [busy, setBusy] = useState<string>('');
    const [revealed, setRevealed] = useState<{ name: string | null; iban: string | null } | null>(null);
    const [matchCands, setMatchCands] = useState<any[] | null>(null);
    const [matchStatus, setMatchStatus] = useState<string | null>(row.match_status ?? null);
    // Plaćatelj: uredi / dupliciraj / podijeli nalog (isplata iz više računa)
    const [tool, setTool] = useState<'' | 'edit' | 'split'>('');
    const [eAmount, setEAmount] = useState(String(row.amount_eur ?? ''));
    const [eSource, setESource] = useState('');       // '' = zadrži trenutni isplatni račun
    const [eIban, setEIban] = useState('');           // '' = ne diraj (IBAN je enkriptiran)
    const [eModel, setEModel] = useState(row.payment_model ?? '');
    const [eRef, setERef] = useState(row.payment_reference ?? '');
    const [eOpis, setEOpis] = useState(row.opis_placanja ?? '');
    const [splitPay, setSplitPay] = useState('');

    const isManager = state?.is_manager ?? isAdmin;
    const myId = state?.my_user_id;
    const isRequester = myId != null && row.requester_user_id === myId;
    const canVoditelj = row.status === 'ceka_voditelja' && ((row.i_can_approve ?? false) || isManager);
    const canSpecial = row.status === 'ceka_posebno_odobrenje' && (row.i_can_approve ?? false);
    const canResubmit = row.status === 'vraceno_na_doradu' && (isRequester || isManager);
    const canFinal = isManager && ['executed', 'zavrseno', 'otkazano', 'problematicno'].includes(row.status);
    // Samootkazivanje krivo unesenog zahtjeva dok NIJE realiziran (prije execute) — mail UID 544.
    // Smije podnositelj, netko iz iste poslovnice, ili manager (mora se poklapati s backendom).
    const isOwnBranch = row.branch_dept_id != null && (state?.my_branch_ids ?? []).includes(row.branch_dept_id);
    const canCancel = ['ceka_voditelja', 'ceka_posebno_odobrenje', 'vraceno_na_doradu', 'pending', 'approved'].includes(row.status)
        && (isRequester || isOwnBranch || isManager);
    const isOtkup = row.reason_code === 'otkup_lom' || row.reason_code === 'otkup_invest';
    const canCancelInflow = INFLOW_REASONS.includes(row.reason_code)
        && ['ceka_voditelja', 'ceka_posebno_odobrenje', 'vraceno_na_doradu', 'pending', 'approved'].includes(row.status)
        && (isManager || (state?.my_branch_ids ?? []).includes(row.branch_dept_id));

    const loadMatchCands = async () => {
        try {
            const r = await api<{ candidates: any[]; match_status: string | null }>(`/payment-traffic/requests/${row.id}/match-candidates`, token);
            setMatchCands(r.candidates ?? []);
        } catch (e) { setMatchCands([]); }
    };
    const doMatch = async (txnId: number | null) => {
        try {
            const r = await mutate<{ match_status: string }>(`/payment-traffic/requests/${row.id}/match`, token, 'POST', { txn_id: txnId });
            setMatchStatus(r.match_status); setMatchCands(null);
        } catch (e) { /* */ }
    };

    useEffect(() => {
        void api<{ events: any[]; approvals: any[] }>(`/payment-traffic/requests/${row.id}/history`, token).then(setHistory).catch(() => setHistory(null));
        // Auto-uparivanje s feedom (poslovnica + iznos + ~datum) čim se otvori otkup zahtjev.
        if ((state?.is_manager ?? isAdmin) && (row.reason_code === 'otkup_lom' || row.reason_code === 'otkup_invest')) {
            void loadMatchCands();
        }
    }, [token, row.id]);
    const [qrSvg, setQrSvg] = useState<string | null>(null);
    const [qrInfo, setQrInfo] = useState<{ amount_eur: number; recipient_name: string; sender_iban: string | null } | null>(null);

    const isVirman = row.reason_code === 'virman_klijent' && (Number(row.has_customer_iban) === 1);

    const reveal = async () => {
        setBusy('reveal');
        try {
            const r = await mutate<{ ok: boolean; customer_name: string | null; customer_iban: string | null }>(`/payment-traffic/requests/${row.id}/reveal`, token, 'POST', {});
            setRevealed({ name: r.customer_name, iban: r.customer_iban });
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(''); }
    };

    const generateQr = async () => {
        setBusy('qr');
        try {
            const r = await api<{ ok: boolean; hub3_payload: string; amount_eur: number; recipient_name: string; sender_iban: string | null }>(`/payment-traffic/requests/${row.id}/hub3`, token);
            const svg = await QRCode.toString(r.hub3_payload, { type: 'svg', errorCorrectionLevel: 'M', width: 320, margin: 2 });
            setQrSvg(svg);
            setQrInfo({ amount_eur: r.amount_eur, recipient_name: r.recipient_name, sender_iban: r.sender_iban });
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(''); }
    };

    const [virman, setVirman] = useState<{ xml: string; xml_b64?: string; filename: string; hub3_payload: string; barcode: string | null; instant: boolean; preview: any } | null>(null);
    const [virmanQr, setVirmanQr] = useState<string | null>(null);
    const loadVirman = async () => {
        setBusy('virman');
        try {
            const r = await api<{ ok: boolean; xml: string; xml_b64?: string; filename: string; hub3_payload: string; barcode: string | null; instant: boolean; preview: any }>(`/payment-traffic/requests/${row.id}/virman`, token);
            setVirman(r);
            if (!r.barcode) setVirmanQr(await QRCode.toString(r.hub3_payload, { type: 'svg', errorCorrectionLevel: 'M', width: 320, margin: 2 }));
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(''); }
    };
    const downloadVirman = () => {
        if (!virman) return;
        // Datoteka mora izaći kao ISO-8859-2 (Erste HR SEPA čita 8859-2, UTF-8 kvari opis → NARR).
        // xml_b64 su ISO-8859-2 bajtovi; dekodiraj base64 → Uint8Array bez reinterpretacije kao UTF-8.
        // Fallback na .xml (UTF-8) samo ako stariji backend ne vrati xml_b64.
        let blob: Blob;
        if (virman.xml_b64) {
            const bin = atob(virman.xml_b64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            blob = new Blob([bytes], { type: 'application/xml' });
        } else {
            blob = new Blob([virman.xml], { type: 'application/xml' });
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = virman.filename; a.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    // Overlay preview kontrolnog dokumenta (fetch s tokenom → blob → iframe).
    const [proofPreview, setProofPreview] = useState<string | null>(null);
    const openProofPreview = async () => {
        try {
            const r = await fetch(`/api/payment-traffic/requests/${row.id}/proof`, { headers: { Authorization: 'Bearer ' + token } });
            if (r.status === 410) { alert(t('Kontrolni dokument je istekao — čuva se 7 dana pa se briše.', 'Control document expired — kept 7 days then removed.')); return; }
            if (!r.ok) throw new Error('HTTP ' + r.status);
            setProofPreview(URL.createObjectURL(await r.blob()));
        } catch (e) { alert('Ne mogu učitati dokument: ' + (e instanceof Error ? e.message : String(e))); }
    };
    const closeProofPreview = () => { if (proofPreview) URL.revokeObjectURL(proofPreview); setProofPreview(null); };

    // Ispis potvrde o uplati (branded PDF) — poslovnice prilažu uz ponudu i račun.
    const [printingConfirmation, setPrintingConfirmation] = useState(false);
    const printConfirmation = async () => {
        setPrintingConfirmation(true);
        try {
            const r = await fetch(`/api/payment-traffic/requests/${row.id}/confirmation-pdf`, { headers: { Authorization: 'Bearer ' + token } });
            if (!r.ok) throw new Error('HTTP ' + r.status);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `Potvrda-uplate-${row.public_id ?? row.id}.pdf`; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
        } catch (e) { alert(t('Ne mogu izraditi potvrdu: ', 'Cannot create confirmation: ') + (e instanceof Error ? e.message : String(e))); }
        finally { setPrintingConfirmation(false); }
    };

    const act = async (path: string, body: any, key: string) => {
        setBusy(key);
        try { await mutate(`/payment-traffic/requests/${row.id}/${path}`, token, 'POST', body); onClose(true); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(''); }
    };

    // Plaćatelj — spremi izmjene (samo promijenjena polja idu u PATCH).
    const doEdit = async () => {
        const body: any = {};
        if (eAmount !== '' && Number(eAmount) !== Number(row.amount_eur)) body.amount_eur = Number(eAmount);
        if (eSource !== '') body.source_subaccount_id = Number(eSource);
        if (eIban.trim() !== '') body.customer_iban = eIban.trim();
        if (eModel !== (row.payment_model ?? '')) body.payment_model = eModel || null;
        if (eRef !== (row.payment_reference ?? '')) body.payment_reference = eRef || null;
        if (eOpis !== (row.opis_placanja ?? '')) body.opis_placanja = eOpis || null;
        if (Object.keys(body).length === 0) { alert(t('Nema promjena.', 'No changes.')); return; }
        setBusy('edit');
        try { await mutate(`/payment-traffic/requests/${row.id}`, token, 'PATCH', body); onClose(true); }
        catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(''); }
    };

    // Plaćatelj — dupliciraj u novi nezavisni zahtjev.
    const doDuplicate = async () => {
        if (!confirm(t('Duplicirati ovaj nalog u novi zahtjev?', 'Duplicate this order into a new request?'))) return;
        setBusy('dup');
        try {
            const r = await mutate<{ public_id: string }>(`/payment-traffic/requests/${row.id}/duplicate`, token, 'POST', {});
            alert(t('Kreiran novi nalog: ', 'New order created: ') + r.public_id);
            onClose(true);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(''); }
    };

    // Plaćatelj — podijeli: plati dio sada, ostatak → novi nalog.
    const doSplit = async () => {
        const pay = Number(splitPay); const total = Number(row.amount_eur);
        if (!(pay > 0) || pay >= total) { alert(t('Iznos „plati sada" mora biti veći od 0 i manji od ukupnog.', 'Pay-now must be > 0 and < total.')); return; }
        setBusy('split');
        try {
            const r = await mutate<{ remainder: number; child_public_id: string }>(`/payment-traffic/requests/${row.id}/split`, token, 'POST', { pay_now_eur: pay });
            alert(t('Podijeljeno. Ostatak ', 'Split done. Remainder ') + fmtEur(r.remainder) + t(' → novi nalog ', ' → new order ') + r.child_public_id);
            onClose(true);
        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(''); }
    };

    const reasonMeta = REASON_META[row.reason_code];
    const statusMeta = STATUS_META[row.status];

    return (
        <Modal onClose={() => onClose(false)} wide>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>{row.public_id}</h3>
                    {row.parent_public_id ? (
                        <span title={t('Ostatak nastao dijeljenjem naloga', 'Remainder created by splitting an order')}
                            style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 6, fontWeight: 700, fontSize: '0.74rem' }}>
                            ↳ {t('dio naloga', 'part of')} {row.parent_public_id}
                        </span>
                    ) : null}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ background: statusMeta.bg, color: statusMeta.color, padding: '3px 10px', borderRadius: 6, fontWeight: 700 }}>
                        {locale === 'en' ? statusMeta.en : statusMeta.hr}
                    </span>
                    {state?.is_owner && row.deleted_at ? (
                        <button type="button" disabled={busy !== ''} title={t('Vrati zahtjev iz koša', 'Restore from trash')}
                            onClick={async () => {
                                setBusy('del');
                                try { await mutate(`/payment-traffic/requests/${row.id}/restore`, token, 'POST', {}); onClose(true); }
                                catch (e) { alert(e instanceof Error ? e.message : 'API'); setBusy(''); }
                            }}
                            style={{ background: '#1f7a36', border: '1px solid #1f7a36', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>
                            ↩ {t('Vrati', 'Restore')}
                        </button>
                    ) : state?.is_owner ? (
                        <button type="button" disabled={busy !== ''} title={t('Obriši zahtjev (samo vlasnik, u koš)', 'Delete request (owner only, to trash)')}
                            onClick={async () => {
                                if (!confirm(t(`Obrisati zahtjev ${row.public_id}? Ide u koš, može se vratiti.`, `Delete ${row.public_id}? Goes to trash, restorable.`))) return;
                                setBusy('del');
                                try { await mutate(`/payment-traffic/requests/${row.id}`, token, 'DELETE'); onClose(true); }
                                catch (e) { alert(e instanceof Error ? e.message : 'API'); setBusy(''); }
                            }}
                            style={{ background: 'transparent', border: '1px solid #b14040', color: '#b14040', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}>
                            🗑 {t('Obriši', 'Delete')}
                        </button>
                    ) : null}
                </div>
            </div>

            {row.duplicate_of_public_id ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', background: '#fdf0e2', border: '1px solid #b8621a', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                    <span style={{ color: '#7a3e0f', fontWeight: 700, fontSize: '0.86rem' }}>
                        ⚠ {t('Ovaj dokument je već ranije unesen u sustav.', 'This document was already entered into the system before.')}
                    </span>
                    <button type="button"
                        onClick={() => { if (onJumpToDuplicate) { onJumpToDuplicate(row.duplicate_of_public_id as string); } else { onClose(false); } }}
                        style={{ background: '#b8621a', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                        {t('Otvori raniju pojavnost', 'Open earlier occurrence')} → {row.duplicate_of_public_id}
                    </button>
                </div>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.86rem', background: '#fafaf6', padding: 12, borderRadius: 8 }}>
                <div><strong>{t('Poslovnica:', 'Branch:')}</strong> {row.branch_name ?? '—'}</div>
                <div><strong>{t('Razlog:', 'Reason:')}</strong> {reasonMeta.icon} {locale === 'en' ? reasonMeta.en : reasonMeta.hr}</div>
                <div><strong>{t('Iznos:', 'Amount:')}</strong> {fmtEur(row.amount_eur)}</div>
                <div><strong>{t('Zatražio:', 'Requested by:')}</strong> {row.requester_name ?? '—'}</div>
                <div style={{ gridColumn: 'span 2' }}><strong>{t('Isplata na:', 'Payout to:')}</strong> {payoutToCell(row, locale)} {row.target_iban ? <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{row.target_iban}</code> : null}</div>
                {row.reason_notes ? <div style={{ gridColumn: 'span 2' }}><strong>{t('Bilješke:', 'Notes:')}</strong> {row.reason_notes}</div> : null}
                {row.proof_document_path ? (
                    <div style={{ gridColumn: 'span 2' }}>
                        <strong>{t('Kontrolni dokument:', 'Control doc:')}</strong>{' '}
                        <a href="#" onClick={(e) => { e.preventDefault(); void openProofPreview(); }}
                            style={{ color: '#1f7a36', textDecoration: 'underline', fontWeight: 600, cursor: 'pointer' }}>
                            📄 {row.proof_document_name ?? 'document'}
                        </a>
                        <span style={{ fontSize: '0.72rem', color: '#999', marginLeft: 8 }}>· {t('čuva se 7 dana', 'kept 7 days')}</span>
                    </div>
                ) : row.proof_document_name ? (
                    <div style={{ gridColumn: 'span 2' }}>
                        <strong>{t('Kontrolni dokument:', 'Control doc:')}</strong>{' '}
                        <span style={{ color: '#999', fontWeight: 600 }}>📄 {row.proof_document_name}</span>
                        <span style={{ fontSize: '0.72rem', color: '#b14040', marginLeft: 8 }}>· {t('istekao (čuvano 7 dana)', 'expired (kept 7 days)')}</span>
                    </div>
                ) : null}
                {(row.reason_code === 'provjera_uplate' || ['received_branch', 'zavrseno', 'executed', 'approved'].includes(row.status)) ? (
                    <div style={{ gridColumn: 'span 2' }}>
                        <button type="button" disabled={printingConfirmation} onClick={() => void printConfirmation()}
                            style={{ padding: '5px 12px', background: '#6b4f1c', color: '#fffbeb', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                            {printingConfirmation ? '⏳ …' : '🖨 ' + t('Ispis potvrde o uplati', 'Print payment confirmation')}
                        </button>
                        <span style={{ fontSize: '0.72rem', color: '#999', marginLeft: 8 }}>· {t('za priložiti uz ponudu i račun', 'attach to offer & invoice')}</span>
                    </div>
                ) : null}
            </div>

            {/* Plaćatelj — uredi / dupliciraj / podijeli nalog (isplata iz više računa) */}
            {isManager && !INFLOW_REASONS.includes(row.reason_code) && !['rejected', 'otkazano'].includes(row.status) ? (
                <div style={{ marginTop: 12, padding: 12, background: '#f0f9ff', border: '1px solid #bae0f5', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#0369a1', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        🧰 {t('Nalog (plaćatelj)', 'Order (payer)')}
                        <button type="button" onClick={() => setTool(tool === 'edit' ? '' : 'edit')} style={{ ...PT_BTN.b, padding: '4px 10px' }}>✏️ {t('Izmijeni', 'Edit')}</button>
                        <button type="button" disabled={busy !== ''} onClick={() => void doDuplicate()} style={{ ...PT_BTN.b, padding: '4px 10px' }}>⎘ {t('Dupliciraj', 'Duplicate')}</button>
                        {['pending', 'approved', 'executed'].includes(row.status) ? (
                            <button type="button" onClick={() => setTool(tool === 'split' ? '' : 'split')} style={{ ...PT_BTN.o, padding: '4px 10px' }}>✂️ {t('Podijeli (plati dio)', 'Split (pay part)')}</button>
                        ) : null}
                    </div>

                    {tool === 'edit' ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                            <label style={{ fontSize: '0.78rem' }}>{t('Iznos €', 'Amount €')}
                                <input type="number" step="0.01" value={eAmount} onChange={(e) => setEAmount(e.target.value)} style={{ width: '100%', padding: 6, boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ fontSize: '0.78rem' }}>{t('Isplatni račun (naš)', 'Payout account (ours)')}
                                <select value={eSource} onChange={(e) => setESource(e.target.value)} style={{ width: '100%', padding: 6, boxSizing: 'border-box' }}>
                                    <option value="">{t('— zadrži trenutni —', '— keep current —')}</option>
                                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.bank_name} · {a.account_label} · …{a.iban.slice(-6)}</option>)}
                                </select>
                            </label>
                            <label style={{ fontSize: '0.78rem', gridColumn: 'span 2' }}>{t('IBAN klijenta (upiši za promjenu)', 'Client IBAN (type to change)')}
                                <input type="text" value={eIban} onChange={(e) => setEIban(e.target.value)} placeholder="HR…" style={{ width: '100%', padding: 6, boxSizing: 'border-box', fontFamily: 'ui-monospace, monospace' }} />
                            </label>
                            <label style={{ fontSize: '0.78rem' }}>{t('Model', 'Model')}
                                <input type="text" value={eModel} onChange={(e) => setEModel(e.target.value)} placeholder="HR99" style={{ width: '100%', padding: 6, boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ fontSize: '0.78rem' }}>{t('Poziv na broj', 'Reference')}
                                <input type="text" value={eRef} onChange={(e) => setERef(e.target.value)} style={{ width: '100%', padding: 6, boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ fontSize: '0.78rem', gridColumn: 'span 2' }}>{t('Opis plaćanja', 'Payment description')}
                                <input type="text" value={eOpis} onChange={(e) => setEOpis(e.target.value)} style={{ width: '100%', padding: 6, boxSizing: 'border-box' }} />
                            </label>
                            <div style={{ gridColumn: 'span 2' }}>
                                <button type="button" disabled={busy !== ''} onClick={() => void doEdit()} style={PT_BTN.dark}>💾 {t('Spremi izmjene', 'Save changes')}</button>
                            </div>
                        </div>
                    ) : null}

                    {tool === 'split' ? (
                        <div style={{ marginTop: 6 }}>
                            <div style={{ fontSize: '0.82rem', color: '#555', marginBottom: 6 }}>
                                {t('Plati DIO s ovog naloga sada; ostatak ide u novi nalog (isti klijent/otkup, spreman za drugi račun).', 'Pay PART from this order now; the remainder goes to a new order (same client/buy, ready for another account).')}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input type="number" step="0.01" value={splitPay} onChange={(e) => setSplitPay(e.target.value)} placeholder={t('Plati sada €', 'Pay now €')} style={{ width: 140, padding: 6 }} />
                                <span style={{ fontSize: '0.86rem', color: '#0369a1', fontWeight: 700 }}>
                                    {splitPay && Number(splitPay) > 0 && Number(splitPay) < Number(row.amount_eur)
                                        ? t('Ostatak: ', 'Remainder: ') + fmtEur(Number(row.amount_eur) - Number(splitPay)) + ' → ' + t('novi nalog', 'new order')
                                        : t('od ', 'of ') + fmtEur(row.amount_eur)}
                                </span>
                                <button type="button" disabled={busy !== '' || !(Number(splitPay) > 0 && Number(splitPay) < Number(row.amount_eur))} onClick={() => void doSplit()} style={PT_BTN.dark}>✂️ {t('Podijeli', 'Split')}</button>
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* VIRMAN — PII sekcija (maskirana, reveal po klikom) */}
            {isVirman ? (
                <div style={{ marginTop: 12, padding: 14, background: 'rgba(177,64,64,0.05)', border: '1px solid rgba(177,64,64,0.3)', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#b14040', fontSize: '0.88rem', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>🔒 {t('Virman — podaci klijenta (enkriptirano)', 'Virman — customer details (encrypted)')}</span>
                        {isAdmin && !revealed ? (
                            <button type="button" disabled={busy === 'reveal'} onClick={() => void reveal()}
                                style={{ background: '#b14040', color: '#fff', border: 0, borderRadius: 6, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                                {busy === 'reveal' ? '…' : t('🔓 Pokaži (audit log)', '🔓 Reveal (audited)')}
                            </button>
                        ) : null}
                    </div>
                    {revealed ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.86rem' }}>
                            <div><strong>{t('Ime:', 'Name:')}</strong> {revealed.name ?? '—'}</div>
                            <div><strong>{t('IBAN:', 'IBAN:')}</strong> <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>{revealed.iban ?? '—'}</code></div>
                            {row.customer_address ? <div><strong>{t('Adresa:', 'Address:')}</strong> {row.customer_address}</div> : null}
                            {row.customer_city ? <div><strong>{t('Grad:', 'City:')}</strong> {row.customer_city}</div> : null}
                            {row.payment_model ? <div><strong>{t('Model:', 'Model:')}</strong> {row.payment_model}</div> : null}
                            {row.payment_reference ? <div><strong>{t('Poziv na broj:', 'Reference:')}</strong> <code>{row.payment_reference}</code></div> : null}
                            {row.opis_placanja ? <div style={{ gridColumn: 'span 2' }}><strong>{t('Opis:', 'Description:')}</strong> {row.opis_placanja}</div> : null}
                        </div>
                    ) : (
                        <div style={{ fontSize: '0.82rem', color: '#888' }}>
                            {t('Ime i IBAN skriveni. Klik "Pokaži" da otkriješ (zapisuje se u audit log).', 'Name and IBAN hidden. Click "Reveal" to disclose (logged to audit).')}
                        </div>
                    )}

                    {/* HUB3 QR */}
                    {isAdmin ? (
                        <div style={{ marginTop: 12, borderTop: '1px solid rgba(177,64,64,0.15)', paddingTop: 10 }}>
                            {!qrSvg ? (
                                <button type="button" disabled={busy === 'qr'} onClick={() => void generateQr()}
                                    style={{ background: '#1e40af', color: '#fff', border: 0, borderRadius: 6, padding: '8px 18px', fontSize: '0.86rem', fontWeight: 700, cursor: 'pointer' }}>
                                    {busy === 'qr' ? '…' : t('📱 Generiraj HUB3 QR kod za skeniranje', '📱 Generate HUB3 QR code')}
                                </button>
                            ) : (
                                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                                    <div dangerouslySetInnerHTML={{ __html: qrSvg }} style={{ background: '#fff', padding: 8, borderRadius: 8, border: '1px solid #ddd' }} />
                                    <div style={{ flex: 1, fontSize: '0.86rem' }}>
                                        <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>📱 {t('HUB3 spreman za skeniranje', 'HUB3 ready to scan')}</div>
                                        <div>{t('Iznos:', 'Amount:')} <strong>{fmtEur(qrInfo?.amount_eur ?? 0)}</strong></div>
                                        <div>{t('Primatelj:', 'Recipient:')} <strong>{qrInfo?.recipient_name}</strong></div>
                                        {qrInfo?.sender_iban ? <div>{t('S podračuna:', 'From sub-account:')} <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{qrInfo.sender_iban}</code></div> : null}
                                        <div style={{ fontSize: '0.74rem', color: '#666', marginTop: 6 }}>
                                            {t('Otvori mobilnu bankarsku app → "Skeniraj račun" → fotografiraj ovaj QR. Sve će se popuniti.', 'Open mobile bank app → "Scan invoice" → photograph this QR. Everything will be filled in.')}
                                        </div>
                                        <button type="button" onClick={() => setQrSvg(null)}
                                            style={{ marginTop: 8, background: 'transparent', border: '1px solid #888', color: '#666', borderRadius: 4, padding: '3px 10px', fontSize: '0.74rem', cursor: 'pointer' }}>
                                            {t('Sakrij QR', 'Hide QR')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* Voditelj odluka */}
            {canVoditelj ? (
                <div style={{ marginTop: 16, padding: 12, background: '#fdf6e8', border: '1px solid #e6cf9a', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#9a6a1a', marginBottom: 8 }}>👤 {t('Odluka voditelja', 'Lead decision')}</div>
                    <input type="text" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} placeholder={t('Napomena (opcionalno)', 'Note (optional)')} style={{ width: '100%', padding: 6, marginBottom: 8, boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('voditelj-decision', { decision: 'approve', note: decisionNote || null }, 'v')} style={PT_BTN.g}>✓ {t('Odobri', 'Approve')}</button>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('voditelj-decision', { decision: 'return', note: decisionNote || null }, 'v')} style={PT_BTN.o}>↩ {t('Vrati na doradu', 'Return')}</button>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('voditelj-decision', { decision: 'reject', note: decisionNote || null }, 'v')} style={PT_BTN.r}>✕ {t('Odbij', 'Reject')}</button>
                    </div>
                </div>
            ) : null}

            {/* Posebno odobrenje (Slaven / Vlado) */}
            {canSpecial ? (
                <div style={{ marginTop: 12, padding: 12, background: '#f5f0ff', border: '1px solid #d3c4f0', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>⭐ {t('Posebno odobrenje', 'Special approval')}</div>
                    <input type="text" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} placeholder={t('Napomena (opcionalno)', 'Note (optional)')} style={{ width: '100%', padding: 6, marginBottom: 8, boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('special-approve', { decision: 'approve', note: decisionNote || null }, 's')} style={PT_BTN.g}>✓ {t('Odobri', 'Approve')}</button>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('special-approve', { decision: 'return', note: decisionNote || null }, 's')} style={PT_BTN.o}>↩ {t('Vrati na doradu', 'Return')}</button>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('special-approve', { decision: 'reject', note: decisionNote || null }, 's')} style={PT_BTN.r}>✕ {t('Odbij', 'Reject')}</button>
                    </div>
                </div>
            ) : null}

            {/* Podnositelj — dorada i ponovno slanje */}
            {canResubmit ? (
                <div style={{ marginTop: 12, padding: 12, background: '#fff3e6', border: '1px solid #e6b98a', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#c2701a', marginBottom: 8 }}>↩ {t('Dorada i ponovno slanje', 'Fix and resubmit')}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="number" value={resubmitAmount} onChange={(e) => setResubmitAmount(e.target.value)} placeholder={t('Iznos €', 'Amount €')} style={{ width: 120, padding: 6 }} />
                        <input type="text" value={resubmitNotes} onChange={(e) => setResubmitNotes(e.target.value)} placeholder={t('Bilješke', 'Notes')} style={{ flex: 1, minWidth: 160, padding: 6 }} />
                        <button type="button" disabled={busy !== ''} onClick={() => void act('resubmit', { amount_eur: Number(resubmitAmount), reason_notes: resubmitNotes || null }, 'rs')} style={PT_BTN.b}>↪ {t('Pošalji ponovno', 'Resubmit')}</button>
                    </div>
                </div>
            ) : null}

            {/* Manager (računovodstvo) — provjera uplate → „Uplata je vidljiva" + napomena */}
            {isManager && INFLOW_REASONS.includes(row.reason_code) && !['zavrseno', 'otkazano'].includes(row.status) ? (
                <div style={{ marginTop: 12, padding: 12, background: '#eef2ff', border: '1px solid #cdd7f5', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 8 }}>💰 {t('Provjera uplate', 'Payment check')}</div>
                    <input type="text" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder={t('Napomena (opcionalno)', 'Note (optional)')} style={{ width: '100%', padding: 6, marginBottom: 8, boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('inflow-confirmed', { note: adminNotes || null }, 'inflow')} style={PT_BTN.b}>✅ {t('Uplata je vidljiva', 'Payment visible')}</button>
                    </div>
                </div>
            ) : null}

            {canCancelInflow ? (
                <div style={{ marginTop: 12, padding: 12, background: '#f5f5f5', border: '1px solid #ccc', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#555', marginBottom: 8 }}>{t('Otkazivanje najave', 'Cancel request')}</div>
                    <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={1000}
                        placeholder={t('Razlog otkazivanja (obavezno)', 'Cancellation reason (required)')}
                        style={{ width: '100%', minHeight: 64, padding: 6, marginBottom: 8, boxSizing: 'border-box' }} />
                    <button type="button" disabled={busy !== '' || cancelReason.trim() === ''}
                        onClick={() => void act('cancel', { reason: cancelReason.trim() }, 'cancel')} style={PT_BTN.gray}>
                        ⊘ {t('Otkaži najavu', 'Cancel request')}
                    </button>
                </div>
            ) : null}

            {/* Manager — izvršenje (osim priljeva) */}
            {isManager && !INFLOW_REASONS.includes(row.reason_code) && (row.status === 'approved' || row.status === 'pending') ? (
                <div style={{ marginTop: 12, padding: 12, background: '#fff8e7', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: 'var(--ad-acc-3)', marginBottom: 8 }}>{t('Nakon izvršenja u net bankingu:', 'After executing in net banking:')}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="text" value={risRef} onChange={(e) => setRisRef(e.target.value)} placeholder={t('RIS referenca broj', 'RIS reference')} style={{ width: 150, padding: 6, fontFamily: 'ui-monospace, monospace' }} />
                        <input type="text" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder={t('Bilješke (opcionalno)', 'Notes (optional)')} style={{ flex: 1, minWidth: 140, padding: 6 }} />
                        <button type="button" disabled={busy !== ''} onClick={() => void act('execute', { ris_reference: risRef || null, admin_notes: adminNotes || null }, 'execute')} style={{ ...PT_BTN.dark, whiteSpace: 'nowrap' }}>🚀 {t('Označi izvršeno', 'Mark executed')}</button>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('reject', { rejection_reason: adminNotes || null }, 'reject')} style={PT_BTN.r}>✕ {t('Odbij', 'Reject')}</button>
                    </div>
                </div>
            ) : null}

            {/* Samootkazivanje krivo unesenog zahtjeva (dok nije realiziran) — mail UID 544. */}
            {canCancel ? (
                <div style={{ marginTop: 12, padding: 12, background: '#fdf2f2', border: '1px solid #edc7c7', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#b14040', marginBottom: 8 }}>⊘ {t('Otkaži krivo unesen zahtjev', 'Cancel wrongly entered request')}</div>
                    <input type="text" value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} placeholder={t('Razlog (opcionalno)', 'Reason (optional)')} style={{ width: '100%', padding: 6, marginBottom: 8, boxSizing: 'border-box' }} />
                    <button type="button" disabled={busy !== ''}
                        onClick={() => {
                            if (!confirm(t(`Otkazati zahtjev ${row.public_id}? Ovo se ne može poništiti.`, `Cancel request ${row.public_id}? This cannot be undone.`))) return;
                            void act('cancel-request', { note: cancelNote || null }, 'cancel');
                        }}
                        style={PT_BTN.r}>⊘ {t('Otkaži zahtjev', 'Cancel request')}</button>
                    <span style={{ fontSize: '0.72rem', color: '#999', marginLeft: 8 }}>· {t('samo dok nije izvršen/obrađen', 'only while not yet executed')}</span>
                </div>
            ) : null}

            {/* Manager — finalni status */}
            {canFinal ? (
                <div style={{ marginTop: 12, padding: 12, background: '#eef7f0', border: '1px solid #bfe3cd', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#1f7a36', marginBottom: 8 }}>{t('Finalni status', 'Final status')}</div>
                    <input type="text" value={finalNote} onChange={(e) => setFinalNote(e.target.value)} placeholder={t('Napomena (za problematično obavezno)', 'Note (required for problematic)')} style={{ width: '100%', padding: 6, marginBottom: 8, boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('final-status', { status: 'zavrseno', note: finalNote || null }, 'f')} style={PT_BTN.g}>✅ {t('Završeno', 'Done')}</button>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('final-status', { status: 'otkazano', note: finalNote || null }, 'f')} style={PT_BTN.gray}>⊘ {t('Otkazano', 'Cancelled')}</button>
                        <button type="button" disabled={busy !== ''} onClick={() => void act('final-status', { status: 'problematicno', note: finalNote || null }, 'f')} style={PT_BTN.r}>⚠ {t('Problematično', 'Problematic')}</button>
                    </div>
                </div>
            ) : null}

            {/* Poslovnica - potvrda primitka u blagajnu. SAMO kad novac stvarno slijeće u blagajnu
                poslovnice (gotovinski/interni tipovi). Za transakcijska plaćanja (has_customer_iban=1,
                virman izravno na račun klijenta) poslovnica nema što primiti - zatvara ga onaj tko je
                izvršio plaćanje (Virman i plaćanje / Finalni status panel niže). Bug: mail UID 540,
                Deni Kocijancic (Osijek-Županijska, PR-2026-0289, ADOnline transakcijski otkup). */}
            {isRequester && row.status === 'executed' && Number(row.has_customer_iban) !== 1 ? (
                <div style={{ marginTop: 12, padding: 12, background: '#e8f5ee', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#1f7a36', marginBottom: 8 }}>{t('Poslovnica: nakon primitka u blagajnu:', 'Branch: after receiving in cash desk:')}</div>
                    <button type="button" disabled={busy !== ''} onClick={() => void act('receive', {}, 'receive')} style={PT_BTN.g}>✅ {t('Primljeno u blagajnu', 'Received in cash desk')}</button>
                </div>
            ) : null}

            {/* Poslovnica - transakcijsko plaćanje: samo obavijest, bez akcije. Isplata ide izravno
                na račun klijenta (virman), ne u blagajnu poslovnice. Zahtjev zatvara osoba koja je
                izvršila plaćanje. */}
            {isRequester && row.status === 'executed' && Number(row.has_customer_iban) === 1 ? (
                <div style={{ marginTop: 12, padding: 12, background: '#eef2ff', border: '1px solid #cdd7f5', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>📨 {t('Transakcijsko plaćanje - izvršeno', 'Bank transfer - executed')}</div>
                    <div style={{ fontSize: '0.82rem', color: '#444' }}>
                        {t('Isplata ide izravno na račun klijenta, ne u blagajnu poslovnice. Nemate daljnju akciju - zahtjev zatvara osoba koja je izvršila plaćanje.', 'Payout goes directly to the client\u2019s account, not the branch cash desk. No further action needed - the person who executed the payment closes the request.')}
                    </div>
                </div>
            ) : null}

            {/* Virman i plaćanje — na račun klijenta (transakcijski) ILI interni na podračun poslovnice (gotovinski/dnevna) */}
            {isManager && !['rejected', 'otkazano'].includes(row.status) && (Number(row.has_customer_iban) === 1 || (!INFLOW_REASONS.includes(row.reason_code) && (row.target_bank_account_id != null || INTERNAL_PAYOUT_REASONS.includes(row.reason_code)))) ? (
                <div style={{ marginTop: 12, padding: 12, background: '#f4f8ff', border: '1px solid #cdd9f0', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700, color: '#1e40af' }}>💳 {t('Virman i plaćanje', 'Payment order')} <span style={{ background: '#1e40af', color: '#fff', fontSize: '0.64rem', padding: '1px 6px', borderRadius: 4, marginLeft: 4 }}>{Number(row.has_customer_iban) === 1 ? 'SEPA' : t('INTERNI', 'INTERNAL')}</span></div>
                        {!virman ? <button type="button" disabled={busy === 'virman'} onClick={() => void loadVirman()} style={{ ...PT_BTN.b, padding: '5px 12px' }}>{busy === 'virman' ? '…' : '⚡ ' + t('Pripremi plaćanje', 'Prepare payment')}</button> : null}
                    </div>
                    {virman ? (
                        <div style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <div style={{ flex: '1 1 280px', minWidth: 260, background: '#fff', border: '1px solid #dbe3f2', borderRadius: 8, padding: 12, fontSize: '0.82rem' }}>
                                <div style={{ fontSize: '0.68rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>{t('Nalog za plaćanje (SEPA instant)', 'Payment order (SEPA instant)')}</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 8px' }}>
                                    <span style={{ color: '#888' }}>{t('Platitelj', 'Debtor')}</span><strong>{virman.preview.debtor_name}{virman.preview.debtor_bank ? ' · ' + virman.preview.debtor_bank : ''}</strong>
                                    <span style={{ color: '#888' }}>{t('S računa', 'From')}</span><code style={{ fontSize: '0.76rem' }}>{virman.preview.debtor_iban}</code>
                                    <span style={{ color: '#888' }}>{t('Primatelj', 'Creditor')}</span><strong>{virman.preview.creditor_name}</strong>
                                    <span style={{ color: '#888' }}>{t('Na račun', 'To')}</span><code style={{ fontSize: '0.76rem' }}>{virman.preview.creditor_iban}</code>
                                    <span style={{ color: '#888' }}>{t('Iznos', 'Amount')}</span><strong style={{ color: '#1f7a36', fontSize: '1rem' }}>{fmtEur(virman.preview.amount)}</strong>
                                    <span style={{ color: '#888' }}>{t('Opis', 'Description')}</span><span>{virman.preview.description}</span>
                                </div>
                                <button type="button" onClick={downloadVirman} style={{ ...PT_BTN.dark, padding: '6px 12px', marginTop: 10 }}>⬇ {t('Preuzmi virman (XML)', 'Download SEPA XML')}</button>
                                <div style={{ marginTop: 4, fontSize: '0.7rem', color: '#888' }}>{t('Upload XML u net banking ILI skeniraj QR u mobilnoj banci.', 'Upload XML to net banking OR scan the QR in mobile banking.')}</div>
                            </div>
                            {virman.barcode ? (
                                <div style={{ textAlign: 'center', flex: '1 1 320px' }}>
                                    <img src={virman.barcode} alt="HUB3 PDF417" style={{ background: '#fff', padding: 8, borderRadius: 8, border: '1px solid #ddd', maxWidth: '100%' }} />
                                    <div style={{ fontSize: '0.7rem', color: '#888', marginTop: 4 }}>📱 {t('Skeniraj 2D barkod u banci (HUB3 / PDF417)', 'Scan 2D barcode in bank app (HUB3 / PDF417)')}</div>
                                </div>
                            ) : virmanQr ? (
                                <div style={{ textAlign: 'center' }}>
                                    <div dangerouslySetInnerHTML={{ __html: virmanQr }} style={{ background: '#fff', padding: 8, borderRadius: 8, border: '1px solid #ddd', width: 180 }} />
                                    <div style={{ fontSize: '0.7rem', color: '#888', marginTop: 4 }}>📱 {t('Skeniraj u banci', 'Scan in bank app')}</div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* P5: Kontrola otkupa (uparivanje s feedom) */}
            {isManager && isOtkup ? (
                <div style={{ marginTop: 12, padding: 12, background: '#f7f5ff', border: '1px solid #ddd6f3', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700, color: '#5b4b9a' }}>🔍 {t('Kontrola otkupa', 'Buy reconciliation')}</div>
                        <div style={{ fontSize: '0.82rem' }}>
                            {matchStatus && MATCH_META[matchStatus]
                                ? <span style={{ color: MATCH_META[matchStatus].color, fontWeight: 700 }}>{MATCH_META[matchStatus].icon} {(locale === 'en' ? MATCH_META[matchStatus].en : MATCH_META[matchStatus].hr)}</span>
                                : <span style={{ color: '#999' }}>{t('nije provjereno', 'not checked')}</span>}
                        </div>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" onClick={() => void loadMatchCands()} style={{ ...PT_BTN.b, padding: '5px 12px' }}>🔎 {t('Nađi u feedu', 'Find in feed')}</button>
                        {matchStatus === 'matched' || matchStatus === 'manual' ? <button type="button" onClick={() => void doMatch(null)} style={{ ...PT_BTN.gray, padding: '5px 12px' }}>✕ {t('Ukloni uparivanje', 'Unmatch')}</button> : null}
                    </div>
                    {matchCands ? (
                        <div style={{ marginTop: 8, border: '1px solid #e6e0f5', borderRadius: 6, maxHeight: 200, overflowY: 'auto', background: '#fff' }}>
                            {matchCands.length === 0 ? <div style={{ padding: 8, color: '#999', fontSize: '0.8rem' }}>{t('Nema kandidata u feedu (±tolerancija, ±1 dan).', 'No feed candidates (±tolerance, ±1 day).')}</div>
                                : matchCands.map((c) => (
                                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '5px 10px', borderBottom: '1px solid #f3f3f3', fontSize: '0.8rem' }}>
                                        <span><strong>{fmtEur(c.amount_eur)}</strong> · {c.kasa_name || c.kasa || '—'} · {fmtDateTime(c.txn_at)} · <code style={{ color: '#888' }}>{c.doc_ref}</code></span>
                                        <button type="button" onClick={() => void doMatch(c.id)} style={{ ...PT_BTN.g, padding: '3px 10px', fontSize: '0.76rem' }}>{t('Upari', 'Match')}</button>
                                    </div>
                                ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* Problem napomena */}
            {row.problem_note ? <div style={{ marginTop: 12, color: '#b14040', fontSize: '0.82rem' }}>⚠ <strong>{t('Problem:', 'Problem:')}</strong> {row.problem_note}</div> : null}
            {row.status === 'rejected' && row.rejection_reason ? <div style={{ marginTop: 12, color: '#b14040', fontSize: '0.82rem' }}>⚠ <strong>{t('Razlog odbijanja:', 'Reason:')}</strong> {row.rejection_reason}</div> : null}

            {/* Timeline (append-only događaji) */}
            {history && history.events.length > 0 ? (
                <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 10 }}>
                    <div style={{ fontWeight: 700, color: 'var(--ad-ink)', marginBottom: 6, fontSize: '0.84rem' }}>🕐 {t('Tijek', 'Timeline')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem' }}>
                        {history.events.map((e, i) => (
                            <div key={i} style={{ color: '#555' }}>
                                <span style={{ color: '#999' }}>{fmtDateTime(e.created_at)}</span> · <strong>{ptEventLabel(String(e.event), locale)}</strong>
                                {e.actor_name ? <span style={{ color: '#888' }}> — {e.actor_name}</span> : null}
                                {e.note ? <span style={{ color: '#777' }}> · {e.note}</span> : null}
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button type="button" onClick={() => onClose(false)} style={{ background: 'var(--ad-ink)', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>{t('Zatvori', 'Close')}</button>
            </div>

            {/* Overlay preview kontrolnog dokumenta (PDF u iframe) */}
            {proofPreview ? (
                <div onClick={closeProofPreview} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: '92vw', maxWidth: 1000, height: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid #eee' }}>
                            <strong style={{ fontSize: '0.9rem' }}>📄 {row.proof_document_name ?? t('Dokument', 'Document')}</strong>
                            <button type="button" onClick={closeProofPreview} style={{ background: 'transparent', border: 0, cursor: 'pointer', fontSize: '1.2rem', color: '#888', fontWeight: 800 }}>✕</button>
                        </div>
                        <iframe src={proofPreview} title="doc-preview" style={{ flex: 1, border: 0, width: '100%' }} />
                    </div>
                </div>
            ) : null}
        </Modal>
    );
}

function Modal({ children, onClose, wide }: { children: React.ReactNode; onClose: () => void; wide?: boolean }) {
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 24, maxWidth: wide ? 940 : 700, width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
                {children}
            </div>
        </div>
    );
}

// ── P3: zvono s gongom + heartbeat prisutnosti (poll svakih 20 s, heartbeat 60 s) ──
type PtNotif = { id: number; request_id: number | null; kind: string; title: string; body: string | null; gong: number | boolean; created_at: string };

function PtNotifBell({ token, locale, state, onActivity }: { token: string; locale: Locale; state: State | null; onActivity?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [notifs, setNotifs] = useState<PtNotif[]>([]);
    const [open, setOpen] = useState(false);
    const [prefs, setPrefs] = useState({ gong: true, email: false, sms: false });
    const onActivityRef = React.useRef(onActivity);
    onActivityRef.current = onActivity;
    useEffect(() => {
        if (state) setPrefs({ gong: !!state.pt_notify_gong, email: !!state.pt_notify_email, sms: !!state.pt_notify_sms });
    }, [state?.pt_notify_gong, state?.pt_notify_email, state?.pt_notify_sms]);
    const togglePref = (k: 'gong' | 'email' | 'sms', v: boolean) => {
        setPrefs((p) => ({ ...p, [k]: v }));
        void mutate('/payment-traffic/me/notifications', token, 'PATCH', { [`pt_notify_${k}`]: v }).catch(() => {});
    };
    const lastIdRef = React.useRef<number>(0);
    const audioRef = React.useRef<AudioContext | null>(null);

    // Web Audio gong (triangle 660→523→392), init nakon prvog user gesta (autoplay policy).
    const playGong = useCallback(() => {
        try {
            if (!audioRef.current) audioRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
            const ctx = audioRef.current;
            if (ctx.state === 'suspended') void ctx.resume();
            const now = ctx.currentTime;
            [660, 523, 392].forEach((freq, i) => {
                const osc = ctx.createOscillator(); const gain = ctx.createGain();
                osc.type = 'triangle'; osc.frequency.value = freq;
                const start = now + i * 0.16;
                gain.gain.setValueAtTime(0.0001, start);
                gain.gain.exponentialRampToValueAtTime(0.5, start + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, start + 1.2);
                osc.connect(gain); gain.connect(ctx.destination);
                osc.start(start); osc.stop(start + 1.25);
            });
        } catch { /* no audio */ }
    }, []);

    // Heartbeat prisutnosti — svakih 60 s dok je modul otvoren.
    useEffect(() => {
        if (!token) return;
        const beat = () => { void mutate('/payment-traffic/presence', token, 'POST', {}).catch(() => {}); };
        beat();
        const h = window.setInterval(beat, 60000);
        return () => window.clearInterval(h);
    }, [token]);

    // Poll novih notifikacija — svakih 20 s.
    useEffect(() => {
        if (!token) return;
        let alive = true;
        const poll = async () => {
            try {
                const r = await api<{ rows: PtNotif[]; last_id: number }>(`/payment-traffic/notifications/poll?after_id=${lastIdRef.current}`, token);
                if (!alive || !r.rows || r.rows.length === 0) return;
                const fresh = r.rows.filter((n) => n.id > lastIdRef.current);
                if (fresh.length === 0) return;
                lastIdRef.current = r.last_id || Math.max(...fresh.map((n) => n.id));
                setNotifs((prev) => [...fresh, ...prev].slice(0, 30));
                onActivityRef.current?.();   // nova aktivnost → osvježi listu zahtjeva
                if (fresh.some((n) => n.gong)) {
                    playGong();
                    if ('Notification' in window && Notification.permission === 'granted') {
                        try { new Notification(fresh[0].title, { body: fresh[0].body ?? '' }); } catch { /* */ }
                    }
                }
            } catch { /* */ }
        };
        void poll();
        const h = window.setInterval(() => void poll(), 20000);
        return () => { alive = false; window.clearInterval(h); };
    }, [token, playGong]);

    const markSeen = useCallback(async () => {
        const upTo = lastIdRef.current;
        if (upTo > 0) { void mutate('/payment-traffic/notifications/seen', token, 'POST', { up_to_id: upTo }).catch(() => {}); }
        setNotifs([]); setOpen(false);
    }, [token]);

    const requestNotifPerm = () => { if ('Notification' in window && Notification.permission === 'default') void Notification.requestPermission(); };

    return (
        <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => { setOpen((o) => !o); requestNotifPerm(); if (!audioRef.current) playGong(); }}
                title={t('Obavijesti platnog prometa', 'Payment traffic notifications')}
                style={{ position: 'relative', background: notifs.length ? '#fff7e0' : 'transparent', border: '1px solid var(--ad-acc)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: '1rem' }}>
                🔔
                {notifs.length ? <span style={{ position: 'absolute', top: -6, right: -6, background: '#b14040', color: '#fff', borderRadius: 999, fontSize: '0.66rem', fontWeight: 800, padding: '1px 5px' }}>{notifs.length}</span> : null}
            </button>
            {open ? (
                <div style={{ position: 'absolute', right: 0, top: '110%', width: 320, maxHeight: 380, overflowY: 'auto', background: '#fff', border: '1px solid #ddd', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.18)', zIndex: 120, padding: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px 8px' }}>
                        <strong style={{ fontSize: '0.84rem' }}>{t('Obavijesti', 'Notifications')}</strong>
                        {notifs.length ? <button type="button" onClick={() => void markSeen()} style={{ border: 0, background: 'transparent', color: '#1e40af', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700 }}>{t('Označi pročitano', 'Mark read')}</button> : null}
                    </div>
                    {notifs.length === 0 ? <div style={{ color: '#999', fontSize: '0.8rem', padding: '6px' }}>{t('Nema novih obavijesti.', 'No new notifications.')}</div>
                        : notifs.map((n) => (
                            <div key={n.id} style={{ padding: '6px 8px', borderBottom: '1px solid #f3f3f3', fontSize: '0.8rem' }}>
                                <div style={{ fontWeight: 600 }}>{n.title}</div>
                                {n.body ? <div style={{ color: '#777', fontSize: '0.74rem' }}>{n.body}</div> : null}
                            </div>
                        ))}
                    <div style={{ borderTop: '1px solid #eee', marginTop: 6, paddingTop: 8 }}>
                        <div style={{ fontSize: '0.72rem', color: '#999', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4, padding: '0 4px' }}>{t('Moje obavijesti', 'My notifications')}</div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', fontSize: '0.8rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={prefs.gong} onChange={(e) => togglePref('gong', e.target.checked)} /> 🔔 {t('Zvučni gong', 'Sound gong')}
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', fontSize: '0.8rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={prefs.email} onChange={(e) => togglePref('email', e.target.checked)} /> ✉ {t('Email', 'Email')}
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', fontSize: '0.8rem', cursor: state?.sms_configured ? 'pointer' : 'not-allowed', opacity: state?.sms_configured ? 1 : 0.5 }}>
                            <input type="checkbox" checked={prefs.sms} disabled={!state?.sms_configured} onChange={(e) => togglePref('sms', e.target.checked)} /> 📱 {t('SMS', 'SMS')}{!state?.sms_configured ? <span style={{ fontSize: '0.68rem', color: '#b14040' }}> ({t('nije spojen', 'not connected')})</span> : null}
                        </label>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
